import type { Translations } from './de.ts';

const en: Translations = {
    app: {
        name: 'Recap',
        expandSidebar: 'Expand sidebar',
        collapseSidebar: 'Collapse sidebar',
    },
    nav: {
        home: 'Overview',
        activities: 'Activity',
        projects: 'Projects',
        clients: 'Clients',
        recaps: 'Recaps',
        sources: 'Sources',
        favorites: 'Favourites',
        workspace: 'Workspace',
        connections: 'Connections',
        team: 'Team',
        settings: 'Settings',
        userSettings: 'Account settings',
    },
    rail: {
        dueTodos_one: '{{count}} to-do due',
        dueTodos_other: '{{count}} to-dos due',
        drafts_one: '{{count}} draft',
        drafts_other: '{{count}} drafts',
        unfiledSources_one: '{{count}} source not in a recap',
        unfiledSources_other: '{{count}} sources not in a recap',
        acknowledgements_one: '{{count}} waiting for your acknowledgement',
        acknowledgements_other: '{{count}} waiting for your acknowledgement',
        new: 'New since your last visit',
        failingFeeds: 'A connection isn’t working',
    },
    auth: {
        preview: {
            title: 'Website relaunch kick-off',
            summary:
                'Petra Müller and Lena Weber talked about how Bäckerei Müller’s online shop starts: with bread and pastries first, cakes follow in spring. The photos are taken in the bakery in March.',
        },
        oauth: {
            title: 'Connect {{client}} to Recap',
            description:
                '{{client}} will be able to find and read your recaps, add sources, create, edit and publish recaps, tick off to-dos and comment – on your behalf, with your permissions.',
            workspace: 'Workspace',
            readOnly: 'Read only',
            readOnlyHint: '{{client}} won’t be able to change anything.',
            afterwards:
                'You’ll then be taken back to {{host}}. The connection appears in {{workspace}} under “My connections”, where you can disconnect it at any time.',
            allow: 'Allow',
            deny: 'Deny',
            invalidTitle: 'This request is invalid',
            noWorkspaceTitle: 'No workspace',
            noWorkspace:
                'You aren’t a member of any workspace, so there’s nothing an application could access for you.',
        },
        account: 'Account ({{name}})',
        back: 'Back',
        step: 'Step {{step}} of {{count}}',
        signOut: 'Sign out',
        fields: {
            email: 'Email address',
        },
        signIn: {
            title: 'Sign in to Recap',
            description: 'Sign in with Google or your email address. You don’t need a password.',
            descriptionWithoutGoogle: 'Sign in with your email address. You don’t need a password.',
            invitedDescription: 'Sign in to join {{name}}. You don’t need a password.',
            submit: 'Sign in',
            withEmail: 'Sign in with email',
            emailHint: 'We’ll send you a link to sign in.',
            newAccount: 'No account yet? Signing in for the first time creates one.',
            terms: 'Recap’s <terms>terms of service</terms> and <privacy>privacy policy</privacy> apply.',
        },
        verify: {
            title: 'Verify your email address',
            description: 'We’ve sent a code to {{email}}.',
            code: 'Code',
            codeInvalid: 'Enter the 6-digit code.',
            submit: 'Verify',
            sendAgain: 'Resend code',
            sent: 'We’ve sent a new code to {{email}}.',
            changeAddress: 'Use a different address',
            changeTitle: 'Use a different address',
            changeDescription:
                'We’ll send a code to the new address. It replaces your current one once you enter the code.',
            newEmail: 'New email address',
            sendCode: 'Send code',
        },
        code: {
            title: 'Check your inbox',
            description: 'Enter the code we sent to {{email}}, or click “Sign in” in the email.',
            label: 'Code',
            digit: 'Digit {{number}}',
            hint: 'We’ll sign you in as soon as all six digits are in.',
            openInbox: 'Open {{name}}',
            notReceived: 'Didn’t get the code?',
            sendAgain: 'Resend code',
            sent: 'We’ve sent you a new code.',
        },
        link: {
            title: 'Signing you in …',
            failedTitle: 'This link no longer works',
            failedDescription: 'Each link works only once, and for 15 minutes.',
            requestCode: 'Request a new code',
        },
        sso: {
            signInWithGoogle: 'Sign in with Google',
            lastUsed: 'Last used',
            or: 'or',
        },
        setup: {
            continue: 'Continue',
            create: 'Create workspace',
            join: 'Join {{name}}',
            profile: {
                title: 'Tell us your name',
                description: 'That’s all we need for now.',
            },
            workspace: {
                title: 'Set up your workspace',
                description: 'This is how your workspace appears in the app.',
            },
            joining: {
                title: 'Welcome, {{name}}',
                description: 'Join a workspace or create a new one.',
                member: 'You’ve been invited as a member.',
                employee: 'You’ve been invited to read the recaps for {{client}}.',
                join: 'Join',
                create: 'Create a new workspace',
            },
            waitlist: {
                title: 'You’re on the waiting list',
                description:
                    'Recap is invite-only for now. Once your access is approved, we’ll email you at {{email}}.',
                invited: 'Have you been invited to a workspace? Open the link in the invitation.',
            },
        },
        invitation: {
            title: 'Join {{name}}',
            descriptionSignedOut: 'This invitation is for {{email}}. Sign in with this address to join.',
            descriptionSignedIn: 'You’re signed in as {{email}}.',
            descriptionLink: 'Sign in to join.',
            descriptionOtherEmail:
                'This invitation is for {{email}}, but you’re signed in as {{currentEmail}}. Sign out and sign in again with {{email}}.',
            join: 'Join',
            signIn: 'Sign in',
            invalidTitle: 'This invitation link is no longer valid',
            invalidDescription: 'It has already been used or was withdrawn. Ask the workspace owner for a new link.',
        },
        employeeInvitation: {
            title: 'Recaps for {{client}}',
            invitedBy: '{{workspace}} invites you to read the recaps for {{client}}.',
            descriptionLink: 'Sign in to join.',
            invalidDescription:
                'It has already been used, was withdrawn or was replaced by a new one. Ask for a new link.',
        },
    },
    common: {
        toRecaps: 'Go to Recaps',
        audio: {
            play: 'Play',
            loading: 'Loading …',
            failed: 'The recording couldn’t be loaded.',
        },
        users: {
            deleted: 'Deleted user',
            deletedBot: 'Deleted bot',
            disabled: '{{name}} (disabled)',
        },
        dates: {
            today: 'Today',
            tomorrow: 'Tomorrow',
            yesterday: 'Yesterday',
            inSentence: {
                today: 'today',
                tomorrow: 'tomorrow',
                yesterday: 'yesterday',
            },
            thisWeek: 'This week',
        },
        showPassword: 'Show password',
        hidePassword: 'Hide password',
        cancel: 'Cancel',
        back: 'Back',
        close: 'Close',
        copy: 'Copy',
        copied: 'Copied',
        clear: 'Clear',
        selected_zero: 'Nothing selected',
        selected_one: '{{count}} selected',
        selected_other: '{{count}} selected',
        clearSelection: 'Clear selection',
        showAll: 'Show all',
        showLess: 'Show less',
        dangerZone: 'Danger zone',
        moreActions: 'More actions',
        optional: 'optional',
        pageNotFound: 'Page not found',
        somethingWentWrong: 'Something went wrong',
        backToDashboard: 'Back to overview',
        pagination: 'Pages',
        previousPage: 'Previous page',
        nextPage: 'Next page',
        itemRange: '{{first}}–{{last}} of {{count}}',
        sync: 'Fetch',
        removeItem: 'Remove {{item}}',
        syncing: 'Fetching …',
        save: 'Save',
        create: 'Create',
        edit: 'Edit',
        autoSave: 'Saves automatically',
        autoSaving: 'Saving …',
        autoSaved: 'Saved',
        autoSaveFailed: 'Not saved',
        autoSaveRetry: 'Try again',
        advanced: 'Advanced',
        resetFilters: 'Reset filters',
        yes: 'Yes',
        no: 'No',
    },
    viewer: {
        page: 'Page {{current}} of {{count}}',
        zoomIn: 'Zoom in',
        zoomOut: 'Zoom out',
        fit: 'Fit to window',
        loading: 'Loading …',
        loadError: 'The file couldn’t be loaded.',
        noPreview: 'There’s no preview for this file.',
    },
    imageField: {
        remove: 'Remove',
    },
    dropzone: {
        chooseFiles: 'Choose files',
        takePhoto: 'Take photo',
    },
    avatarColor: {
        pick: 'Choose colour',
        none: 'No colour',
        colors: {
            red: 'Red',
            orange: 'Orange',
            amber: 'Amber',
            yellow: 'Yellow',
            lime: 'Lime',
            green: 'Green',
            emerald: 'Emerald',
            teal: 'Teal',
            cyan: 'Cyan',
            sky: 'Sky',
            blue: 'Blue',
            indigo: 'Indigo',
            violet: 'Violet',
            purple: 'Purple',
            fuchsia: 'Fuchsia',
            pink: 'Pink',
            rose: 'Rose',
        },
    },
    errors: {
        failed: 'That didn’t work. Please try again.',
        api: {
            not_signed_in: 'You’ve been signed out. Please sign in again.',
            csrf_failed: 'This page has been open for too long. Please reload it.',
            not_found: 'This couldn’t be found. It may have been deleted in the meantime.',
            owner_only: 'Only the workspace owner can do this.',
            required: 'Required',
            too_long: 'Too long',
            invalid: 'Invalid value',
            invalid_reference: 'This belongs to another workspace. Please reload the page.',
            email_taken: 'This email address is already in use.',
            too_many_attempts: 'Too many attempts. Please wait a few minutes and try again.',
            email_not_sent: 'The email couldn’t be sent. Please try again in a moment.',
            email_not_verified: 'Verify your email address first.',
            approval_pending: 'Your access hasn’t been approved yet. We’ll email you once it is.',
            already_approved: 'Your access has already been approved.',
            wrong_code: 'This code is incorrect.',
            code_expired: 'This code has expired. Request a new one.',
            invitation_for_other_email: 'This invitation is for a different email address.',
            slug_taken: 'This short name is already taken.',
            terms_not_accepted:
                'Accept the terms of service and the data processing agreement to create the workspace.',
            owner_cannot_leave: 'As the owner, you can’t leave the workspace. Transfer it to someone else first.',
            access_disabled: 'Your access to this workspace has been disabled.',
            owner_cannot_be_disabled: 'The owner can’t be disabled.',
            owner_cannot_delete_account: 'Transfer or delete the workspaces you own first.',
            email_incorrect: 'This email address doesn’t belong to your account.',
            sso_denied: 'You cancelled signing in with Google. Try again, or sign in with your email address.',
            sso_expired: 'Signing in with Google took too long. Please try again.',
            sso_email_not_verified:
                'Google hasn’t verified your email address yet. Verify it with Google, then try again.',
            sso_unavailable: 'Google can’t be reached right now. Try again later, or sign in with your email address.',
            already_member: 'This address already belongs to a member.',
            already_invited: 'This address has already been invited.',
            already_employee: 'This address already belongs to one of a client’s people in this workspace.',
            route_overlaps: 'Another rule in this feed already matches the same items.',
            route_query_invalid: "The search query can't be read. The marking in the field shows what to change.",
            feed_taken: 'Another feed of this connection already reads this.',
            todo_output_not_reported:
                'This project’s to-dos don’t go to a tool that can report a task back. Only a webhook can.',
            todos_output_taken:
                'This project already sends its to-dos to another tool. Remove “Send to-dos” there first to send them here.',
            feed_lists_in_use: 'A rule in this feed uses a list that would no longer be read.',
            image_type_not_allowed: 'Use a PNG, JPEG or WebP image.',
            image_not_readable: 'This image can’t be opened.',
            file_type_not_allowed: 'You can upload PDFs, images, emails, recordings, Office documents and text files.',
            file_too_large: 'This file is too large. Recordings can be up to 100 MB, other files up to 20 MB.',
            connection_login_failed: 'The login was rejected. Check the username and password.',
            connection_unreachable: 'The server can’t be reached. Check the server and port.',
            connection_host_not_allowed: 'This server can’t be reached from the internet.',
            connection_unsupported_version: 'This version of Odoo is too old. Recap reads Odoo from version 17 on.',
            connection_api_not_included:
                'Your Odoo.com plan has no external API. Recap can only connect with the “Custom” plan.',
            feed_not_found:
                'This doesn’t exist, or the connection isn’t allowed to read it. For example, the folder isn’t in the mailbox, or the Notion page hasn’t been shared with the integration yet (“•••” › Connections).',
            feed_sync_running: 'This feed is being fetched right now.',
            input_on_own_connection:
                'Your personal connections can only send things out. What comes in belongs to the workspace.',
            connection_scope_missing: 'The connection needs your permission for this. Sign in again to grant it.',
            connection_signed_out: 'This connection is waiting for someone to sign in again.',
            oauth_denied: 'Sign-in was cancelled, so nothing was connected.',
            oauth_admin_consent_required:
                'Your Microsoft account belongs to an organization whose admin has to allow Recap first. Send your admin the link to approve it.',
            oauth_other_account: 'You signed in with a different account from the one this connection uses.',
            oauth_expired: 'Sign-in timed out. Please try again.',
            github_installation_unknown:
                'Your GitHub account can’t access this installation of Recap. Sign in to GitHub with an account that belongs to the organisation.',
            github_install_requested:
                'The organisation has to approve Recap first. Its owners have received your request on GitHub. Once they’ve approved it, connect GitHub again.',
            webhook_rejected: 'The webhook’s signature is invalid.',
            webhook_payload_invalid: 'The data sent to the webhook isn’t a valid source.',
            token_not_allowed: 'This can’t be done with an API key. Sign in to Recap to do it.',
            token_read_only: 'This API key is read-only.',
            bot_taken: 'Another connection already uses this bot.',
            oauth_request_invalid: 'This request doesn’t come from an application Recap can trust.',
            recap_processing: 'This recap is being processed right now.',
            recap_not_publishable:
                'This recap can’t be published right now: it has no draft or no title, or it’s being processed.',
            recap_not_shareable: 'Only a published recap can be shared.',
            recap_draft_not_discardable: 'There’s no draft to discard, or the recap is being processed.',
            recap_version_not_restorable: 'This version can’t be restored right now, or the recap is being processed.',
            source_processing: 'This source is being processed right now.',
            source_has_no_update: 'There’s no update for this source.',
            source_not_found_at_provider:
                'None of your connections can reach this. Has it been deleted, or hasn’t it been shared with Recap?',
            link_connection_missing:
                'Reading this link needs a connection the workspace doesn’t have yet. Connect the service in the settings.',
            source_file_unreadable:
                'This can’t be read. It may be a folder or a form, too large, or a file type Recap can’t read.',
            link_without_text:
                'This page has no text of its own, only subpages. Paste the link to a subpage, or add a feed that reads the page’s subpages.',
            link_unreadable:
                'This link couldn’t be read. The page may not be public, may not respond, may be too large or may have no text.',
            client_has_recaps: 'This client’s projects have recaps.',
            external_company_linked: 'This company is linked to another client already.',
            project_has_recaps: 'This project has recaps.',
            project_without_client:
                'This project isn’t shared with a client. Share it with one in its “Client” tab first.',
            project_not_of_client: 'This project doesn’t belong to the source’s client.',
            not_comment_author: 'Only the author can change this comment.',
            recap_acknowledgers_not_changeable: 'This recap hasn’t been published yet.',
            not_acknowledger: 'You haven’t been asked to acknowledge this recap.',
            not_todo_person: 'Only the person the to-do is assigned to can tick it off.',
            already_acknowledged: 'You’ve already acknowledged this recap.',
        },
    },
    validation: {
        required: 'Required',
        routeCondition: 'Add at least one condition.',
        routeDestination: 'Choose a client or a project.',
        sameList: 'Choose a different list from the one for new cards.',
        email: 'Enter a valid email address.',
        slug: 'Use only lowercase letters, numbers and single hyphens, up to 40 characters.',
        httpsUrl: 'Enter an address that starts with https://.',
    },
    // A new workspace's start on the overview (plan task 124).
    start: {
        step: 'Step {{step}} of 5',
        steps: {
            forward: 'Forward',
            clients: 'Clients',
            invite: 'Invite',
            publish: 'Publish',
            connect: 'Connect',
        },
        skip: 'Skip',
        hide: 'Hide the start',
        forward: {
            title: 'Start with a few email threads',
            lead: 'Forward them to the address below. One is enough to begin with, three or four give better suggestions. From them we work out your clients and projects.',
            window: 'Forward',
            to: 'To',
            subject: 'Subject',
            subjectExample: 'Fwd: Delivery times at checkout',
            waiting: 'Waiting for the first email …',
            sent: 'I’ve forwarded them',
            arrivesSoon: 'Usually arrives within 1–2 minutes.',
            soon: 'The email should be here any moment.',
            late: 'Hmm, this is taking longer than usual.',
            openInbox: 'Open {{name}}',
            noAddress: 'This workspace doesn’t have an email address yet.',
            steps: {
                open: 'Open an email thread with a client.',
                forward: 'Forward it to the address on the left.',
                more: 'Forward two or three more threads if you like.',
            },
        },
        clients: {
            title: 'Your first emails are in',
            lead_one:
                'We found one client in them and sorted its threads into projects. Check that the clients and projects are right.',
            lead_other:
                'We found {{count}} clients in them and sorted their threads into projects. Check that the clients and projects are right.',
            leadNone: 'We haven’t found a client in them yet. Sort the emails yourself or forward a few more.',
            leadSorting:
                'We’re reading them and looking for your clients, their projects and who belongs to them. Only what you confirm is created.',
            leadCreated: 'Your clients are created. The first recaps are being drafted from your emails.',
            reading_one: 'Reading one email …',
            reading_other: 'Reading {{count}} emails …',
            sorting: 'Finding clients and projects …',
            created: 'Created',
            noProjects: 'No projects',
            failed: 'The emails couldn’t be sorted. Sort them yourself below.',
            processing_one: 'One more email is still being read. It shows up here once it’s been read.',
            processing_other: '{{count}} more emails are still being read. They show up here once they’ve been read.',
            changeLater: 'You can change names and where things belong at any time, also once everything is created.',
            rename: 'Rename',
            ignore: 'Ignore',
            create: 'Create',
            projects: 'Projects',
            emails: 'Emails',
            isNew: 'new',
            removeProject: 'Don’t create',
            addProject: 'Add project',
            projectName: 'Project name',
            clientName: 'Client name',
            save: 'Apply',
            cancel: 'Cancel',
            noProject: 'No project',
            newProject: 'New project …',
            toClient: 'To “{{client}}”',
            unassigned: 'No client',
            unassignedHint: 'We can’t tell a company from these senders.',
            chooseClient: 'Choose client',
            newClient: 'New client …',
            newClientName: 'New client',
            clientCount_one: '{{count}} client',
            clientCount_other: '{{count}} clients',
            projectCount_one: '{{count}} project',
            projectCount_other: '{{count}} projects',
            createAll: 'Create all: {{clients}}, {{projects}}',
            next: 'Continue',
        },
        invite: {
            title: 'Invite the people who read along',
            lead: 'We know the addresses from your emails. Whoever you invite now gets your first recap straight away.',
            titleTeam: 'Invite your team',
            leadTeam:
                'Until you’ve created a client, we don’t know anyone there yet. You can already invite your team.',
            team: 'Your team',
            teamHint: 'Invited members write the recaps with you.',
            teamNone: {
                title: 'We didn’t find anyone from your team in your emails',
                description: 'Add members yourself or share the invite link.',
            },
            firstRecap: 'The people you invite get the recap on “{{project}}” first.',
            clientRecaps: 'The people you invite get the recaps of “{{client}}”.',
            invite: 'Invite',
            edit: 'Edit',
            remove: 'Don’t invite',
            addPerson: 'Add person',
            name: 'Name',
            email: 'Email address',
            jobTitle: 'Job title',
            cancel: 'Cancel',
            apply: 'Apply',
            inviteAll_one: 'Invite all: {{count}} person',
            inviteAll_other: 'Invite all: {{count}} people',
            next: 'Continue',
            suggestionsWaiting_one:
                'One suggested client is still waiting. Once you’ve created it, you can invite its people too.',
            suggestionsWaiting_other:
                '{{count}} suggested clients are still waiting. Once you’ve created them, you can invite their people too.',
            showSuggestions: 'View suggestions',
        },
        publish: {
            title: 'Publish your first recap',
            lead: 'Once they’ve accepted your invitation, your clients read it in Recap and acknowledge it with one click.',
            leadAlone: 'You haven’t invited anyone yet. Once you publish it, only your team sees it for now.',
            drafting: 'Your first recap is being drafted from your emails.',
            none: 'There’s no draft yet. It’s drafted once you’ve created a client.',
            decisions: 'Decisions',
            todos: 'To-dos',
            others_one: 'One more draft waits after it',
            others_other: '{{count}} more drafts wait after it',
            publish: 'Publish',
            invite: 'Invite people',
        },
        connect: {
            title: 'Connect your tools',
            lead: 'What happens in your tools then reaches Recap by itself: your recaps get more complete, and you no longer have to forward or upload anything.',
            mailbox: 'Your mailbox',
            mailboxHint: 'New emails come in by themselves, without you forwarding them.',
            otherMailbox: 'Another mailbox',
            otherMailboxAction: 'Set up forwarding',
            assistant: 'Recap in your AI assistant',
            assistantHint: 'It sends sources, reads and creates recaps, and ticks off to-dos.',
            otherAssistant: 'Another assistant',
            otherAssistantAction: 'Connect over MCP',
            connectWith: 'Connect with {{name}}',
            more: 'More tools Recap reads from',
            allConnections: 'All connections',
            reasons: {
                notion: 'Meeting notes and pages become sources.',
                trello: 'Cards become sources, and to-dos become new cards.',
                hubspot: 'Keeps your clients and their contacts up to date.',
            },
            connect: 'Connect',
            connected: 'Connected',
            dismiss: 'Don’t suggest',
            draftsWaiting_one: 'One draft waits under “Recaps” until you publish it.',
            draftsWaiting_other: '{{count}} drafts wait under “Recaps” until you publish them.',
            toRecaps: 'Go to Recaps',
            finish: 'Finish getting started',
        },
    },
    landing: {
        signIn: 'Sign in',
        signUp: 'Request access',
        toDark: 'Switch to the dark theme',
        toLight: 'Switch to the light theme',
        hero: {
            tasks: 'Tasks in',
            talk: 'Messages in',
            files: 'Files in',
            question: 'Updates for the client?',
            questionEnd: 'Written by hand.',
            otherTools: 'Show other apps',
            text: 'Recap turns your emails, notes and transcripts into a recap for you and your client: what was discussed and decided, who does what and which events are coming up. You approve it, and your client acknowledges it in one click.',
        },
        samples: {
            label: 'Example',
            from: 'From',
            showAll: 'Show all',
        },
        how: {
            eyebrow: 'How it works',
            replay: 'Play again',
            title: 'Work the way you always do. Recap does the rest',
            text: 'Work piles things up: emails, agendas, minutes, status updates and notes from meetings and calls. Recap boils it down for your client.',
            incoming: {
                title: 'What builds up anyway',
                text: 'Emails, notes and transcripts come in from the apps you’ve connected. You can upload anything else, including photos and voice notes.',
            },
            drafted: {
                title: 'A draft takes shape',
                text: 'The draft sums up what was discussed, with the decisions, to-dos and events. For each point, it cites the exact passage in the sources.',
                meta: 'From 3 sources, 2 minutes ago',
            },
            published: {
                title: 'You check and publish',
                text: 'You change what doesn’t fit and choose who at your client should acknowledge it. Your client only sees the recap once you publish it.',
                whoAcknowledges: 'Who should acknowledge it?',
                publish: 'Publish version 1',
            },
            acknowledged: {
                title: 'Your client acknowledges',
                text: 'They get an email, read the recap, comment on it and acknowledge it. What it says is then agreed.',
                comments: '2 comments',
            },
            sources: {
                email: 'Re: Relaunch – photos',
                emailMeta: 'Tim Müller · Email',
                notes: 'Kick-off Bäckerei Müller',
                notesMeta: 'Notion · Meeting notes',
                transcript: 'Call about cake pre-orders',
                transcriptMeta: 'Teams · Transcript',
            },
        },
        tools: {
            eyebrow: 'Apps',
            title: 'Integrates seamlessly with the apps you already use',
            text: 'Connect each app once and choose what Recap may read. From then on, anything new lands with the right client by itself.',
            imap: { name: 'Mailbox', text: 'Emails with attachments, from Gmail or any other mailbox' },
            microsoft: {
                name: 'Microsoft 365',
                text: 'Emails from Outlook, transcripts from Teams and files from OneDrive',
            },
            google: { name: 'Google Drive', text: 'Files you pick' },
            notion: { name: 'Notion', text: 'Pages and meeting notes, once nobody is working on them any more' },
            atlassian: {
                name: 'Jira and Confluence',
                text: 'Work items and what has happened to them, plus the pages of a space',
            },
            trello: { name: 'Trello', text: 'Cards with their comments and changes' },
            github: { name: 'GitHub', text: 'Issues and pull requests from the repositories you share' },
            odoo: { name: 'Odoo', text: 'Clients, notes, emails and tasks with their logged time' },
            hubspot: { name: 'HubSpot', text: 'Your clients and their contacts, without adding them again' },
            webhook: { name: 'Webhooks', text: 'Anything Make, n8n or Zapier sends' },
            api: { name: 'API and MCP', text: 'Access to recaps and sources for your own scripts and AI assistants' },
            upload: { name: 'Upload', text: 'PDFs, photos, Office files and voice notes' },
        },
        client: {
            eyebrow: 'For your client',
            title: 'Instead of “How far along are you?”',
            text: 'After every meeting, the same questions come in: What did we discuss, and who’s taking care of what now? With Recap, you and your client can look that up at any time.',
            without: 'Without Recap',
            with: 'With Recap',
            yours: 'Your to-dos',
            acknowledged: '{{name}} acknowledged the recap',
            summary: {
                title: 'What was discussed',
                text: 'As soon as you publish a recap, your client gets an email and can read what was discussed and decided in one click.',
            },
            todos: {
                title: 'What they need to do',
                text: 'Recap automatically reminds your client of to-dos that are due and upcoming events. You don’t have to chase them.',
            },
            acknowledgement: {
                title: 'Acknowledged in one click',
                text: 'Recap records who acknowledged which version and when, so what was agreed is documented for everyone to see.',
            },
        },
        try: {
            title: 'Try Recap with your own emails and notes',
            text: 'After signing up, forward a few emails with your clients to Recap. It recognises your clients and projects from them and drafts your first recap.',
            beta: 'Free while Recap is in beta.',
            access: 'We’re opening new workspaces bit by bit.',
            examples: {
                notes: 'Meeting notes',
                email: 'Email from the client',
                voice: 'Voice memo',
                transcript: 'Call transcript',
                photo: 'Photo of the whiteboard',
                call: 'Notes from a call',
            },
            scraps: {
                notes: '– New home page\n– Shop finder?\n– Live by 1 Dec!',
                emailSubject: 'Re: Relaunch – photos',
                emailText: 'Hi Lena, here are the photos of the shops. The one on the market square is still missing …',
                transcript: '14:32 Sabine: Bread online too? No, that’s too much. Just cakes then.',
                call: 'Sabine, 12 min: prices by Monday',
            },
        },
        footer: {
            text: 'Recap turns what happens in your apps into recaps for your clients.',
            product: 'Recap',
            copyright: '© {{year}} Recap',
            language: 'Language',
        },
        otherLanguage: 'Auf Deutsch lesen',
    },
    legal: {
        title: 'Legal',
        names: {
            privacy: 'Privacy policy',
            imprint: 'Imprint',
            terms: 'Terms of service',
            dpa: 'Data processing agreement',
        },
        date: 'Last updated: {{date}}',
        draft: 'Draft: this text is still being reviewed by a lawyer.',
        summary: 'In short',
        summaryNote: 'The summary explains; the text below is what’s binding.',
        details: 'In detail',
        expandAll: 'Expand all',
        collapseAll: 'Collapse all',
        germanIsBinding: 'The German version is binding.',
        acceptTerms: 'I accept the <terms>terms of service</terms> and the <dpa>DPA</dpa>.',
        settingsHint: 'What applies to Recap and how we handle your data.',
    },
    changelog: {
        title: 'Changelog',
        lead: 'What has changed in Recap, newest first.',
        also: 'Also',
    },
    inviteLink: {
        title: 'Invitation link',
        create: 'Create link',
        renew: 'Create new link',
        turnOff: 'Turn off link',
    },
    members: {
        title: 'Team',
        linkNone: 'Anyone with the link can join as a member with their own account.',
        linkJoins: 'Anyone who opens the link and signs in joins {{workspace}}.',
        hintOwner:
            'Everyone with access to this workspace. As the owner, you invite and manage them. Who works on which client and project is set in the client’s and the project’s “Team” tab.',
        hintMember:
            'Everyone with access to this workspace. Only the owner can invite and manage them. Who works on which client and project is set in the client’s and the project’s “Team” tab.',
        clients: {
            all: 'All clients',
            none: 'Not on any client yet',
        },
        you: 'you',
        roles: {
            owner: 'Owner',
        },
        disabled: 'Disabled',
        disable: {
            action: 'Disable',
            title: 'Disable {{name}}?',
            confirm:
                '{{name}} will no longer be able to open the workspace or receive emails from it. Their personal connections will be paused. Their name stays on everything they did. You can enable their access again at any time.',
            employeeConfirm:
                '{{name}} will no longer be able to open the workspace, receive emails from it or be asked to acknowledge recaps. Their personal connections will be paused. Their name stays on everything they did. You can enable their access again at any time.',
            botConfirm:
                'Its connection’s key will stop working, and Recap will reject every request made with it. The name stays on everything {{name}} did. You can enable the bot again at any time.',
            submit: 'Disable',
        },
        enable: {
            action: 'Enable',
        },
        invited: 'Invited',
        invitedOn: 'Invited on {{date}}',
        revokeInvitation: 'Withdraw invitation',
        copyInvitationLink: 'Copy invitation link',
        sendInvitation: 'Resend',
        invitationSent: 'Invitation sent again',
        fields: {
            email: 'Email address',
        },
        invite: {
            action: 'Invite',
            title: 'Invite member',
            hint: 'We’ll email an invitation to this address. Only someone signed in with this address can join.',
            submit: 'Send invitation',
        },
        link: {
            title: 'Invitation sent',
            hint: 'We’ve emailed the invitation to {{email}}. If it doesn’t arrive, you can share the link yourself – only {{email}} can use it to join.',
            label: 'Invitation link',
        },
        remove: {
            action: 'Remove',
            title: 'Remove member?',
            confirm: '{{name}} will no longer have access to this workspace.',
            submit: 'Remove',
        },
        bot: {
            pill: 'Bot',
            withoutConnection: 'No connection',
            openConnection: 'Open connection',
        },
        deleteBot: {
            action: 'Delete',
            title: 'Delete {{name}}?',
            confirm:
                'Its connection will be deleted and its key will stop working. Everything {{name}} did stays, shown as “Deleted bot”.',
            submit: 'Delete',
        },
        transfer: {
            action: 'Make owner',
            title: 'Transfer workspace?',
            confirm:
                '{{name}} will become the owner and the only person who can invite and remove members and delete the workspace. You’ll stay on as a member.',
            submit: 'Transfer',
        },
        leave: {
            action: 'Leave workspace',
            title: 'Leave workspace?',
            confirm: 'You’ll lose access to {{name}} until the owner invites you again.',
            submit: 'Leave',
        },
    },
    workspaces: {
        accessDisabled: {
            title: 'Access disabled',
            description: 'Your access to {{name}} has been disabled. Ask someone there to enable it again.',
            back: 'Go to your workspaces',
        },
        title: 'Workspace',
        switch: 'Switch workspace (current: {{name}})',
        new: 'New workspace',
        newTitle: 'New workspace',
        details: 'Details',
        shortId: 'ID {{id}}…',
        idHint: 'For the API: it’s part of the endpoint paths and, unlike the short name, never changes.',
        editTitle: 'Edit workspace',
        newHint:
            'A workspace has its own members, connections and recaps – for example for a company or an organisation.',
        fields: {
            id: 'Workspace ID',
            name: 'Name',
            nameHint: 'For example your company, your organisation or your own name',
            slug: 'Short name',
            slugHint: 'Appears in the address of every page: <path />',
            logo: 'Logo',
        },
        logo: {
            choose: 'Choose logo',
            hint: 'Shown instead of the colour. PNG, JPEG or WebP, up to 2 MB.',
        },
        delete: {
            action: 'Delete workspace',
            title: 'Delete workspace?',
            confirm: '{{name}} will be deleted with all its recaps, files and connections. This can’t be undone.',
            typeSlug: 'Type “{{slug}}” to confirm',
            submit: 'Delete permanently',
            blockedTitle: 'Workspace can’t be deleted',
            blocked: 'Only the owner can delete {{name}}.',
        },
    },
    settings: {
        tabs: {
            label: 'Sections',
            general: 'General',
            connectionFailed: 'A connection has an error',
        },
        dangerZone: {
            deleteHint: 'Deletes all recaps, files and connections. Only the owner can do this.',
            leaveHint: 'You’ll lose access to the workspace. Only the owner can delete it.',
        },
    },
    connections: {
        title: 'Connections',
        hint: 'Connect the tools your team works with, for example a mailbox (IMAP). What arrives there becomes sources.',
        empty: 'No connections yet',
        ownTitle: 'My connections',
        membersConnections: 'Members’ connections',
        ownEmpty: 'No connections of your own yet',
        ownHint: 'Connect your own tools, for example your calendar, to receive what’s yours in this workspace there.',
        add: {
            button: 'Add connection',
            title: 'Add connection',
            notSetUp: 'Not set up on this server',
            search: 'Search',
            noResults: 'Nothing found',
            ownTools: 'For your own tools',
            connected: 'Connected',
            all: 'All',
            pickAccount: {
                title: 'Which account?',
                description: '{{entry}} is added to one of these accounts.',
                other: 'Connect another account',
            },
        },
        connection: {
            newTitle: 'Connect {{provider}}',
            editTitle: 'Edit {{provider}}',
            edit: 'Edit connection',
            connect: 'Connect',
            connecting: 'Connecting …',
            test: 'Test connection',
            testing: 'Testing …',
            testSucceeded: 'The connection works. You can save it now.',
            testRequired: 'Test the connection before saving it.',
            noFeeds: {
                description: 'Choose what comes in or goes out through this connection.',
                readOnly: 'Whoever can edit this connection chooses what comes in or goes out through it.',
            },
            toUserSettings: 'Go to account settings',
            picksFiles: 'With this connection, you can pick files from {{provider}} under “Add” on the Sources page.',
            signIn: 'Sign in with {{provider}}',
            signingIn: 'Redirecting …',
            signedOut:
                'The connection to {{provider}} has stopped working, for example because access was revoked. Reconnect it. Until then, nothing is fetched or sent.',
            signInAgain: 'Reconnect',
            signedOutState: 'No longer connected',
            switchAccount: {
                button: 'Connect a different account',
                title: 'Connect a different account?',
                confirm:
                    'The connection will use the account you sign in with at {{provider}}. Its feeds stay, but anything only the previous account can reach will fail at the next fetch.',
                submit: 'Sign in with {{provider}}',
            },
            fields: {
                name: 'Name',
            },
            nameHint: 'What should the connection be called? For example “Accounting”.',
            newNameHint:
                'What should the connection be called? For example “Accounting”. If you leave it empty, it’s named after the account.',
            feedCount_one: '{{count}} feed',
            feedCount_other: '{{count}} feeds',
            clientCount_one: 'for one client',
            clientCount_other: 'for {{count}} clients',
            errorCount_one: 'Error',
            errorCount_other: '{{count}} errors',
            directions: {
                input: 'Incoming',
                output: 'Outgoing',
            },
            delete: {
                button: 'Delete connection',
                title: 'Delete connection?',
                confirm:
                    'Nothing more will be fetched from it. Sources fetched so far stay, along with the recaps made from them.',
                confirmMember:
                    'The connection is {{name}}’s. Recap won’t fetch anything through it or send anything there anymore, and {{name}} gets an email about it. Sources fetched so far stay, along with the recaps made from them.',
                submit: 'Delete',
            },
        },
        feed: {
            pickTitle: 'Add a feed to {{name}}',
            access: {
                hint: 'Recap needs your permission at {{provider}} for this. You’ll sign in there again and then return here.',
                allow: 'Allow access',
            },
            flows: {
                input: '{{name}} → Recap',
                output: 'Recap → {{name}}',
            },
            add: 'Add',
            addMore: 'Add feed',
            fetchesFrom: 'Fetching from {{date}}',
            syncedAt: 'Last fetched {{date}}',
            linkedClients: 'To the linked clients',
            onPublish: 'When published',
            ownRecaps: 'Your recaps',
            allRecaps: 'All recaps',
            everythingElse: 'Everything else',
            syncedUntilHint: 'Pick an earlier date to fetch older items too.',
            defaultClient: 'Default client',
            defaultProject: 'Default project',
            drafting: {
                label: 'Draft a recap',
                hint: 'As soon as the source has been read.',
                none: 'No',
                alone: 'From this source only',
                withWaitingInProject: 'Together with all waiting sources in {{project}}',
                withWaitingOfClient: 'Together with all waiting sources of {{client}} that have no project',
                withWaitingWithoutClient: 'Together with all waiting sources that have no client',
                shownAlone: 'drafts a recap',
                shownWithWaiting: 'drafts a recap with the waiting sources',
            },
            skipsHint: 'Otherwise, only items that match a rule are imported. Nothing else is saved.',
            skipped: 'Skipped',
            skipsEverything: 'Everything is skipped',
            byWebhook: 'Arrives by webhook',
            byForwarding: 'Arrives by forwarding',
            waitsForReconnect: 'Waiting for the connection to be reconnected',
            failed: 'Error',
            fields: {
                synced_until: 'Fetch from',
            },
            routes: {
                add: 'Add rule',
                newTitle: 'Add rule',
                editTitle: 'Edit rule',
                noClient: 'No client',
                quoted: '“{{keyword}}”',
                senders: 'From',
                organizer: 'Organised by',
                attendees: 'Attendees',
                recipients: 'To',
                subjectKeywords: 'Subject',
                bodyKeywords: 'Text',
                fileName: 'File name',
                titleKeywords: 'Title',
                lists: 'List',
                hint: 'A source belongs to this rule when every condition in one of its groups applies; within a condition, one of its entries is enough.',
                hintEmail:
                    'An email belongs to this rule when every condition in one of its groups applies; within a condition, one of its entries is enough.',
                words: 'Has the words',
                notWords: "Doesn't have",
                query: 'Search query',
                addCondition: 'Add',
                conditionSection: 'Condition',
                groupSection: 'Group',
                orGroup: 'Or group',
                or: 'or',
                removeCondition: 'Remove {{field}}',
                convert: 'Convert to search query',
                convertGroup: 'Convert group to search query',
                convertTitle: 'Convert to a search query?',
                convertConfirm:
                    "The conditions become one search query that matches the same emails, and their fields are removed. This can't be undone.",
                convertGroupConfirm:
                    "This group's conditions become one search query that matches the same emails, and their fields are removed. The other groups stay as they are. This can't be undone.",
                convertSubmit: 'Convert',
                querySyntax: {
                    title: 'How to write a search query',
                    from: 'From this domain, address or name',
                    to: 'To this domain, address or name, also in Cc or as Bcc',
                    subject: 'This word in the subject',
                    filename: "This word in an attachment's name",
                    anywhere: 'This word anywhere in the email',
                    phrase: 'Exactly these words',
                    or: 'Either of them',
                    and: 'Both (also with AND)',
                    not: 'Without this word (also with NOT)',
                    field: 'One field for several words',
                },
                queryErrors: {
                    unclosed_bracket: "This bracket isn't closed. Add a “)” where it ends.",
                    unexpected_bracket: 'This bracket has no “(”. Remove it or add a “(” before it.',
                    unclosed_quote: "This quotation mark isn't closed. Add a second one after the words.",
                    unknown_field: "“{{name}}:” isn't a field. Use from:, to:, subject: or filename:.",
                    field_in_field: "A field's brackets can't hold another field. Write the fields next to each other.",
                    missing_value: '“{{text}}” needs something to search for, for example from:acme.de.',
                    dangling_operator: '“{{text}}” needs a term to apply to. Add one or remove “{{text}}”.',
                    too_long: 'A search query has at most 2,000 characters. Shorten it or split it into groups.',
                },
                preview: {
                    emails: 'Matches {{count}} of the last {{total}} emails',
                    emailsNone: 'No emails yet to test the rule on.',
                    sources: 'Matches {{count}} of the last {{total}} sources',
                    sourcesNone: 'No sources yet to test the rule on.',
                    show: 'Test',
                    hide: 'Hide',
                },
                delete: {
                    button: 'Delete',
                    title: 'Delete rule?',
                    confirm: 'Items already assigned keep their client.',
                    submit: 'Delete',
                },
            },
            delete: {
                button: 'Delete',
                title: 'Delete feed?',
                confirm: 'Nothing more will be fetched from it. Sources fetched so far stay.',
                submit: 'Delete',
            },
        },
        facets: {
            email: {
                label: 'Emails',
                keepsEverything: 'Import all incoming emails',
            },
            meeting: {
                label: 'Meetings',
                keepsEverything: 'Import all recorded meetings',
            },
            document: {
                label: 'Documents',
                keepsEverything: 'Import all new documents',
            },
            task: {
                label: 'Tickets',
                keepsEverything: 'Import all tickets',
            },
            conversation: {
                label: 'Conversations',
                keepsEverything: 'Import all conversations',
            },
            calendar: { label: 'Calendar', keepsEverything: 'Import all meetings' },
            events: { label: 'Events' },
            todos: { label: 'To-dos' },
            recaps: { label: 'Recaps' },
            clients: { label: 'Clients' },
        },
        errors: {
            login_failed: 'The last fetch failed: the login was rejected. Check the username and password.',
            unreachable: 'The last fetch failed: the server couldn’t be reached.',
            not_found: 'The last fetch failed: what this feed reads no longer exists.',
            host_not_allowed: 'The last fetch failed: the server can no longer be reached from the internet.',
            sync_failed: 'The last fetch failed because of a problem on our side. Try again.',
            unsupported_version: 'The last fetch failed: Recap doesn’t read this version of the server.',
            api_not_included: 'The last fetch failed: the plan no longer has an external API.',
        },
        google: {
            summary: 'Files from Google Drive that you pick',
            workspaceNote:
                'A Google connection uses a single Google account and can only reach what that account can. For the workspace, connect a shared account (for example team@agency.com) rather than your own. Everyone connects their own Google account in their account settings.',
            description:
                'A Google account. You sign in with Google, and Recap only learns your email address. It only asks for more access when you set up something that needs it.',
        },
        atlassian: {
            summary: 'Jira and Confluence, with your Atlassian sign-in',
            description:
                'An Atlassian account. You sign in at Atlassian and choose the site Recap may read there. To add another site, sign in again and choose it. Recap only asks for what it may read once you add something that needs it. Recap only reads and changes nothing. If you delete the connection, also remove Recap under “Apps” in your Atlassian account, as Atlassian keeps the access until then.',
            workspaceNote:
                'An Atlassian connection uses a single Atlassian account and only reaches what that account can access. So for the workspace, connect a shared account (for example projects@agency.com) rather than your own.',
            entries: {
                task: { name: 'Jira', summary: 'Work items of a project and what happened to them' },
                document: { name: 'Confluence', summary: 'Pages of a space, once nobody is working on them' },
            },
            fields: { jiraProject: 'Jira project', spaceOrPage: 'Space or page' },
            pickProject: 'Choose a Jira project',
            projectsLoading: 'Loading Jira projects …',
            projectHint: 'Is a project missing? Sign in at Atlassian again and choose its site there.',
            spaceOrPageHint:
                'The link of a space or page from your browser’s address bar. For a page, every page below it is read too.',
            settleHint: 'A page is only imported once nobody has edited it for this long.',
            changeTrackingHint:
                'If a page changes later, its source is marked, and you can apply the change on the source.',
            feeds: {
                task: {
                    label: 'Read work items',
                    description:
                        'Every change to a work item in a Jira project becomes a source. What only part of the team may see stays out.',
                },
                document: {
                    label: 'Read pages',
                    description: 'Every page of a space becomes a source once nobody is working on it.',
                },
            },
        },
        github: {
            summary: 'Issues and pull requests from the repositories you choose',
            description:
                'Recap is installed as a GitHub App in your organisation or account, and you choose on GitHub which repositories Recap can read. Recap reads their issues and pull requests, but no code. GitHub also lists write access to issues: Recap only needs it once it adds to-dos as issues.',
            repositoryHint: 'Is a repository missing? <page>Give Recap access on GitHub</page>.',
            pickRepository: 'Choose a repository',
            repositoriesLoading: 'Loading repositories …',
            fields: {
                repository: 'Repository',
            },
            feeds: {
                task: {
                    label: 'Read issues and pull requests',
                    description: 'Every change to an issue or pull request in a repository becomes a source.',
                },
            },
        },
        microsoft: {
            summary: 'Outlook.com and Microsoft 365, with your Microsoft sign-in',
            description:
                'A Microsoft account, Outlook.com or Microsoft 365. You sign in with Microsoft, and Recap only learns who you are. Recap only asks for what it reads once you add something, such as a folder of your mailbox. Recap doesn’t mark anything in your mailbox as read or move it.',
            workspaceNote:
                'A Microsoft 365 connection uses a single Microsoft account and only reaches what that account has access to. For the workspace, connect a shared account (for example projects@agency.com) rather than your own. Everyone connects their own account in their account settings.',
            entries: {
                email: { name: 'Outlook', summary: 'Emails from a folder of your mailbox' },
                calendar: { name: 'Outlook calendar', summary: 'Meetings with an agenda, once they’re over' },
                meeting: {
                    name: 'Teams meetings',
                    summary: 'Transcripts and agendas of your Teams meetings, once they’re over',
                },
                document: {
                    name: 'OneDrive & SharePoint',
                    summary: 'Files from a folder, once nobody is working on them',
                },
                todos: { name: 'Microsoft To Do', summary: 'Your to-dos as tasks in a list, to tick off there' },
            },
            folderLinkHint:
                'The folder’s link from your browser’s address bar, or from “Copy link” in OneDrive or SharePoint. Subfolders are read too.',
            settleHint: 'A file is only imported once nobody has edited it for this long.',
            changeTrackingHint:
                'If a file changes later, its source is marked, and you can apply the change on the source.',
            meetingHint:
                'Teams only writes a transcript when someone starts transcription in the meeting. Without one, a meeting comes in with its agenda once Teams hasn’t written a transcript for two hours.',
            transcriptSteps: {
                title: 'Without connecting: upload the transcript yourself',
                open: 'In Teams, open the meeting’s chat or calendar entry, then “Recap” › “Transcript”.',
                download: 'Choose “Download”, as .docx or .vtt.',
                upload: 'Upload the file in Recap under “Sources” › “Add” and choose the meeting’s client and project.',
            },
            fields: { calendar: 'Calendar', folderLink: 'Folder', todoList: 'List' },
            pickCalendar: 'Choose a calendar',
            calendarsLoading: 'Loading calendars …',
            calendarHint: 'Meetings without an agenda, cancelled ones and those you declined are left out.',
            pickTodoList: 'Choose a list',
            todoListsLoading: 'Loading lists …',
            todoListHint:
                'Your open to-dos go into this list as tasks. When you tick a task off there, the to-do is done in Recap too.',
            folderHint: 'Best a folder of its own that a rule in Outlook moves the relevant emails into.',
            ownNote:
                'What Recap reads from your mailbox belongs to the workspace: everyone who sees the client sees these emails too.',
            signInInstead: 'You connect Outlook by signing in with Microsoft, without a password.',
            signIn: 'Sign in with Microsoft',
            adminConsentLink: 'Link to approve',
            feeds: {
                email: {
                    description: 'Every email in a folder of the mailbox becomes a source.',
                },
                calendar: {
                    label: 'Read meetings',
                    description: 'Every meeting with an agenda becomes a source once it’s over.',
                },
                meeting: {
                    label: 'Read Teams meetings',
                    description:
                        'Every Teams meeting becomes a source, with its transcript once Teams has written it. Your organisation’s admin usually has to allow Recap to read transcripts.',
                    describe: 'Your Teams meetings',
                },
                todos: {
                    label: 'Send to-dos',
                    description: 'Your to-dos from published recaps become tasks in a list in Microsoft To Do.',
                },
                document: {
                    label: 'Read files',
                    description:
                        'Every file in a folder becomes a source once nobody is working on it. Word, Excel, PowerPoint, PDFs and images.',
                },
            },
        },
        trello: {
            description:
                'A Trello account. Recap reads its boards and can add the to-dos of published recaps to them as cards.',
            signIn: 'Connect with Trello',
            signedIn: 'Signed in to Trello',
            signInHint: 'Sign in to Trello in the window that opens and allow Recap access. Then save.',
            signInAgainHint: 'Only sign in again if access was revoked or you want to connect a different account.',
            notSignedIn: 'Sign in to Trello first.',
            boardHint: 'The board’s link from your browser’s address bar.',
            pickList: 'Choose a list',
            doneListHint: 'Cards in this list count as done, and to-dos that are ticked off are moved here.',
            listsHint:
                'If you don’t choose any, all lists are read. A card that leaves a chosen list is still imported.',
            listsLoading: 'Loading lists …',
            attachmentsHint:
                'Files uploaded to a card since the last fetch are imported with it: PDFs, images, Office documents and text files up to 20 MB.',
            listsNeedBoard: 'Enter the board’s link first.',
            fields: {
                board: 'Board',
                lists: 'Lists',
                attachments: 'Import attachments',
                list: 'Add new cards to',
                doneList: 'Done list',
            },
            feeds: {
                task: {
                    label: 'Read cards',
                    description: 'Every change to a card on a board becomes a source.',
                },
                todos: {
                    label: 'Add to-dos as cards',
                    description: 'The to-dos of a project’s published recaps become cards on a board.',
                },
            },
        },
        notion: {
            description:
                'A Notion workspace, through an internal integration. Recap only reads pages and never changes them.',
            tokenHint: 'Leave empty to keep the saved token.',
            newTokenHint:
                'Create an internal integration with “Read content” at notion.so/profile/integrations and copy its token. Then share every page Recap should read with it (“•••” › Connections).',
            pageHint: 'The page’s link, from “Copy link” in Notion. The page has to be shared with the integration.',
            depthHint: 'With one level, the page works like a folder: each subpage becomes a source.',
            settleHint: 'A page is only imported once nobody has edited it for this long.',
            changeTrackingHint:
                'If a page changes later, its source is marked, and you can apply the change on the source.',
            fields: {
                token: 'Token',
                page: 'Page',
                depth: 'Subpages',
                settle: 'Import once unchanged for',
                changeTracking: 'Detect changes',
            },
            depths: {
                only: 'Only this page',
                one: 'One level',
                two: 'Two levels',
                three: 'Three levels',
                all: 'All levels',
            },
            settle: {
                now: 'Immediately',
                quarterHour: '15 minutes',
                hour: '1 hour',
                threeHours: '3 hours',
                day: '1 day',
            },
            feeds: {
                document: {
                    label: 'Read pages',
                    description: 'A page, or each of its subpages, becomes a source.',
                },
            },
        },
        hubspot: {
            description:
                'Recap is installed as an app in your HubSpot account. It asks for what it may read only when you add something, such as your customers. Recap only reads and changes nothing in HubSpot.',
            entries: {
                clients: { name: 'HubSpot customers', summary: 'Your customers and their contacts from HubSpot' },
            },
            fields: {
                lifecycleStages: 'Lifecycle stages',
            },
            lifecycleStagesLoading: 'Loading lifecycle stages …',
            lifecycleStagesHint:
                'Companies in these stages come in as customers. Add the stages before it if you work on a project before it’s won.',
            feeds: {
                clients: {
                    label: 'Read customers',
                    description:
                        'Your customers from HubSpot with their contacts. A company that clearly matches a client in Recap is linked to it; you can add or link the others under “Clients”.',
                },
            },
        },
        odoo: {
            description:
                'Your Odoo on Odoo.com, Odoo.sh or a server of your own, through a user’s API key. Recap only reads and never changes anything in Odoo.',
            entries: {
                clients: { name: 'Odoo customers', summary: 'Your customers and their contacts from Odoo' },
                conversation: { name: 'Odoo messages', summary: 'Notes, messages and emails about your customers' },
                task: { name: 'Odoo projects', summary: 'A project’s tasks and the time logged on them' },
                document: {
                    name: 'Odoo Documents',
                    summary: 'Files from a folder in Odoo Documents, Odoo Enterprise only',
                },
            },
            steps: {
                user: 'In Odoo, create a user of its own for Recap under “Settings” › “Users”. Recap reads everything this user may see, so only give it the rights for what Recap should read.',
                key: 'Sign in as this user and choose “My Profile” › “Account Security” › “New API Key” at the top right. Pick the longest duration Odoo offers: once the key expires, Recap stops fetching.',
                enter: 'Enter Odoo’s address, the user’s login and the key here.',
            },
            planNote:
                'On Odoo.com, only the “Custom” plan has the external API. With “Standard” or “One App Free”, Recap can’t connect; Odoo.sh and servers of your own always work.',
            serverHint: 'The address you open Odoo at in your browser, for example https://agency.odoo.com.',
            databaseHint:
                'On Odoo.com and on a server with only one database, you can leave this empty. On Odoo.sh, the name is in the project’s overview.',
            loginHint: 'The login of Recap’s user, usually its email address.',
            apiKeyHint: 'Leave empty to keep the stored key.',
            fields: {
                server: 'Server',
                database: 'Database',
                login: 'User',
                apiKey: 'API key',
                tags: 'Tags',
                models: 'Read from',
                project: 'Project',
                folder: 'Folder',
            },
            models: {
                partner: 'Contacts',
                lead: 'Leads and opportunities',
                order: 'Quotations and orders',
                project: 'Projects',
            },
            pickProject: 'Choose a project',
            pickFolder: 'Choose a folder',
            foldersLoading: 'Loading folders …',
            folderHint: 'Subfolders are read too.',
            documentsMissing: 'Odoo Documents is part of Odoo Enterprise only. The app isn’t installed on this server.',
            settleHint: 'A file is only imported once nobody has worked on it for this long.',
            changeTrackingHint:
                'If a file changes later, its source is marked, and you can take the change on the source.',
            projectsLoading: 'Loading projects …',
            modelsHint: 'Only what’s installed in Odoo. Internal notes come along.',
            tagsLoading: 'Loading tags …',
            tagsHint:
                'Only customers with one of these tags. Without any, every company that bought from you comes in.',
            feeds: {
                clients: {
                    label: 'Read customers',
                    description:
                        'Your customers from Odoo with their contacts. A company that clearly matches a client in Recap is linked to it; you can add or link the others under “Clients”.',
                    everyCustomer: 'All customers',
                },
                document: {
                    label: 'Read files',
                    description:
                        'Every file in a folder of Odoo Documents becomes a source once nobody is working on it anymore. If its contact is linked to a client, it goes to that client.',
                },
                task: {
                    label: 'Read tasks',
                    description:
                        'Every task of a project that something happened to becomes a source: with its comments, changes and the time logged on it.',
                },
                conversation: {
                    label: 'Read messages',
                    description:
                        'What was written on a record (notes, messages, emails, logged calls) becomes a source at each fetch. If its company is linked to a client, it goes to that client.',
                },
            },
        },
        api: {
            description:
                'For your own scripts, n8n and anything else that can call an API: a key gives access to Recap’s API. The workspace’s connection acts as a bot that appears among the members; your personal one acts on your behalf, with your permissions.',
            name: 'API (Developers)',
            summary: 'A key for scripts, n8n and Claude Code',
            fields: {
                readOnly: 'Read only: the key can’t change anything',
                bot: 'Bot',
            },
            botHint: 'An existing bot keeps its name and its history.',
            newBot: 'New bot',
            token: {
                title: 'Your API key',
                warning:
                    'Keep it somewhere safe, such as your password manager. Anyone who has it can do whatever the connection is allowed to do. If you lose it, create a new one.',
                copy: 'Copy key',
                saved: 'I’ve saved the key',
                usage: 'How to call the API',
                docsLead: 'All endpoints:',
                docs: 'API documentation',
            },
            details: {
                token: 'Key',
                actsAs: 'Acts as',
                actsAsYou: 'You, with your permissions',
                access: 'Permissions',
                readOnly: 'Read only',
                readWrite: 'Read and write',
                usedAt: 'Last used',
                neverUsed: 'Never',
            },
            bot: {
                disabledTitle: 'The bot “{{name}}” is disabled',
                disabled:
                    'The key won’t work until you enable the bot again. Until then, Recap rejects every request made with it.',
            },
            usage: {
                renew: 'Create new key',
                apiTitle: 'Using the API',
                api: 'For your own scripts, n8n, Make and anything else that calls an API: send requests to {{address}} with the key in the “Authorization: Bearer …” header.',
                keyNote:
                    'Use the full key instead of “{{prefix}}…”. Recap only shows it once, when it’s created. Lost it? Create a new one.',
                docs: 'Open API documentation',
                mcpTitle: 'Using MCP',
                mcp: 'For AI assistants: Recap’s MCP server at {{address}} gives them tools such as “Search recaps” and “Tick off to-do”, with this connection’s permissions.',
                madeBy: '{{client}} created this connection when it signed in over MCP, and uses it itself. Deleting it disconnects {{client}}.',
                claudeCode: {
                    name: 'Claude Code',
                    hint: 'in the terminal',
                    command: 'In the terminal:',
                    check: 'In Claude Code, run “/mcp” to check that “recap” is connected.',
                },
                other: {
                    name: 'Cursor and others',
                    hint: 'with the key in the header',
                    config: 'Add it as an MCP server over HTTP, for example in Cursor in ~/.cursor/mcp.json:',
                    signIn: 'A client that can sign in by itself (OAuth) doesn’t need a key and creates its own connection.',
                },
                signIn: {
                    name: 'Claude and ChatGPT',
                    hint: 'sign in by themselves',
                    text: 'Claude (claude.ai, desktop, app) and ChatGPT sign in to Recap by themselves and create their own connection, so they don’t need this key. You’ll find instructions under “Add connection › AI assistants (MCP)”.',
                },
            },
            renew: {
                title: 'Create a new key?',
                confirm: 'The current key will stop working immediately.',
                submit: 'Create new key',
            },
        },
        caldav: {
            name: 'Calendar and Reminders (CalDAV)',
            summary: 'Events and to-dos in Apple Calendar, Reminders and Thunderbird, to tick off there',
            description:
                'An account for Apple Calendar and Reminders, Thunderbird or Android (with DAVx⁵). It holds the events from your published recaps and all your to-dos. When you tick a to-do off there, it’s ticked off in Recap too.',
            password: {
                title: 'Your app password',
                warning:
                    'Enter it in your calendar app right away or keep it in your password manager. Anyone who knows it together with your email address can see your events and to-dos. If you lose it, create a new one.',
                copy: 'Copy app password',
                saved: 'I’ve saved the app password',
            },
            details: {
                server: 'Server',
                serverHint: 'For iPhone, iPad, Mac and Fantastical.',
                serverUrl: 'Full address',
                serverUrlHint: 'For Thunderbird and Android.',
                username: 'Username',
                password: 'App password',
                usedAt: 'Last used',
                neverUsed: 'Never',
            },
            setup: {
                title: 'Set up',
                lead: 'Add a CalDAV account in your calendar app, with the server, your email address as the username and the app password. In the app you can tick to-dos off and reopen them. Anything else you change about a to-do or an event there, Recap sets back to what was published.',
                iphone: {
                    name: 'iPhone and iPad',
                    step1: 'Open Settings › Apps › Calendar › Calendar Accounts › Add Account › Other.',
                    step2: 'Tap “Add CalDAV Account” and enter the server, username and app password.',
                    step3: 'The events then appear in Calendar and the to-dos in Reminders.',
                },
                mac: {
                    name: 'Mac',
                    step1: 'Open Calendar › Settings › Accounts and click “+” › “Other CalDAV Account”.',
                    step2: 'Choose “Manual” as the account type and enter the username, app password and server.',
                },
                thunderbird: {
                    name: 'Thunderbird',
                    step1: 'In the calendar, click “New Calendar” › “On the Network”.',
                    step2: 'Enter the username, and the full address under “Location”, then the app password when Thunderbird asks for it.',
                },
                android: {
                    name: 'Android',
                    step1: 'Install DAVx⁵ and add an account with “Login with URL and user name”, with the full address as the URL.',
                    step2: 'For the to-dos you also need a task app such as jtx Board or Tasks.org.',
                },
                others: 'Google Calendar and Outlook can’t add CalDAV accounts. Use the calendar subscription there.',
            },
            renew: {
                action: 'Create new app password',
                title: 'Create a new app password?',
                confirm:
                    'The current app password stops working straight away. Enter the new one in every app where you’ve set up the account.',
            },
        },
        token: {
            warningTitle: 'You’ll only see this once',
            done: 'Done',
        },
        mcp: {
            name: 'AI assistants (MCP)',
            description: 'Claude, ChatGPT, Claude Code and others',
            title: 'Use Recap in AI assistants',
            pick: 'With MCP, an AI assistant can work in your workspace with your permissions. Where do you want to use Recap?',
            clientTitle: 'Recap in {{client}}',
            address: 'MCP address',
            done: 'Done',
            signedIn:
                'The connection then appears under “My connections”, named after the assistant. You can disconnect it there.',
            clients: {
                claude: { name: 'Claude', hint: 'claude.ai, desktop, app' },
                chatgpt: { name: 'ChatGPT', hint: 'chatgpt.com, desktop' },
                claudeCode: { name: 'Claude Code', hint: 'in the terminal' },
                other: { name: 'Others', hint: 'Cursor, MCP Inspector …' },
            },
            claude: {
                open: 'In Claude, go to:',
                path: 'Settings › Connectors › Add custom connector',
                url: 'Enter “Recap” as the name and this URL:',
                signIn: 'Click “Connect”, sign in to Recap, choose the workspace and allow access.',
            },
            chatgpt: {
                open: 'In ChatGPT, turn on developer mode:',
                path: 'Settings › Apps & Connectors › Advanced',
                url: 'Click “Create”, enter “Recap” as the name, choose “OAuth” for authentication and enter this URL:',
                signIn: 'Sign in to Recap, choose the workspace and allow access.',
            },
            claudeCode: {
                key: 'Claude Code signs in with the key of an API connection. Create one for it:',
                create: 'Create API connection',
                existing: 'You can also use the key of an API connection you already have.',
                command: 'In the terminal, with your key instead of “rcp_…”:',
            },
            other: {
                url: 'Add this address as an MCP server over HTTP:',
                signIn: 'If the client can sign in (OAuth), it takes you to Recap. Otherwise, it sends the key of an API connection in the “Authorization: Bearer rcp_…” header, like Claude Code.',
            },
        },
        forwarding: {
            howTo: 'Instructions',
            setup: 'Set up forwarding',
            copyAddress: 'Copy address',
            name: 'Mailbox (forwarding)',
            summary: 'No password, with rules in Recap',
            description:
                "Your mailbox forwards every email to an address of Recap's. Which client and project an email belongs to is decided by the rules here, not by filters in the mailbox. Recap doesn't need a password for it.",
            ownNote:
                'What your mailbox forwards to Recap belongs to the workspace: everyone who sees the client also sees these emails.',
            address: {
                hint: 'The rules below decide which client and project a forwarded email belongs to.',
            },
            gmail: {
                title: 'Gmail',
                steps: {
                    add: 'Add the address above as a forwarding address under <page>Settings › Forwarding and POP/IMAP</page>.',
                    confirm:
                        'Confirm with the link Recap emails you, then forward a copy of incoming mail to the address.',
                },
            },
            outlook: {
                title: 'Outlook',
                steps: {
                    enable: 'In Outlook on the web, turn on forwarding to the address above under Settings › Mail › Forwarding.',
                },
                note: 'Many Microsoft 365 organizations block forwarding outside the organization. If nothing arrives, your IT has to allow it for your mailbox.',
            },
            feeds: {
                email: {
                    label: 'Receive emails',
                    description: "Every forwarded email goes through this mailbox's rules.",
                },
            },
        },
        webhook: {
            description:
                'For Make, Activepieces, n8n, Zapier and anything else that can call a URL: data sent to a Recap URL becomes a source, and published recaps and their to-dos are sent to a URL of your choice.',
            inputUrl: 'Webhook URL',
            inputUrlHint:
                'Send JSON to this URL with POST, containing text, files as URLs, or both. Anyone who knows the URL can add sources. To replace it, delete the feed and add it again.',
            inputPending: 'Once the feed has been added, you’ll find the URL under “…” › Edit.',
            outputUrlHint:
                'The URL Recap sends JSON to with POST, for example a “Custom webhook” URL in Make. HTTPS only.',
            signingSecret: 'Signing secret',
            signingSecretHint:
                'Every request includes the header “X-Recap-Signature: sha256=…”: the HMAC-SHA256 of its body, signed with this secret.',
            example: 'Example',
            report: {
                title: 'Report a task back to Recap',
                hint: 'If your tool makes a task of a to-do, it can report it back with a key from the “API (Developers)” connection. The to-do on the recap then links to it, and the next events about that to-do include its ID under “external”.',
                key: '<API key>',
            },
            copyUrl: 'Copy URL',
            fields: {
                url: 'URL',
                events: 'Events',
            },
            events: {
                todo: {
                    created: 'Created: a to-do appears in a published version for the first time',
                    changed: 'Changed: a new version changes the to-do, or it’s ticked off or reopened',
                    removed: 'Removed: the to-do is no longer in any published version',
                },
                recap: {
                    created: 'Created: a recap is created in the project, manually or from sources',
                    drafted: 'Drafted: a recap’s draft is ready, including when it’s redrafted',
                    changed: 'Changed: a recap’s draft was edited (sent at most once every 5 minutes)',
                    published: 'Published: a version is published',
                    removed: 'Removed: a recap is deleted or moved to another project',
                },
            },
            feeds: {
                document: {
                    label: 'Receive sources',
                    description: 'Data your tool sends to a Recap URL becomes a source.',
                },
                todos: {
                    label: 'Send to-dos',
                    description: 'The to-dos of a project’s published recaps are sent to a URL of your choice.',
                },
                recaps: {
                    label: 'Send recaps',
                    description:
                        'A project’s recaps are sent to a URL of your choice when they’re created, drafted, edited and published.',
                },
            },
        },
        ics: {
            description:
                'A calendar address for Google Calendar, Apple Calendar or Outlook that works without signing in. It contains the events from published recaps and, if you like, open to-dos on their due date.',
            subscribe: 'Subscribe in calendar',
            everything: 'All events',
            address: 'Calendar address',
            open: 'Open in calendar',
            pending: 'Once the subscription has been added, you’ll find the address under “…” › Edit.',
            ownTodos: 'Include my open to-dos on their due date',
            teamTodos: 'Include open to-dos on their due date, with the person they’re assigned to',
            howTo: {
                apple: 'Apple Calendar and Outlook on desktop: click “Open in calendar”.',
                google: 'Google Calendar: “Other calendars” › “From URL”, then paste the address. Google can take a few hours to show changes.',
                outlook: 'Outlook on the web: “Add calendar” › “Subscribe from web”, then paste the address.',
            },
            secretNote:
                'Anyone who knows this address can see everything in the calendar. Only share it with the people it’s meant for.',
            caldavHint:
                'On an iPhone, a Mac or in Thunderbird, you can also see your to-dos as reminders and tick them off there.',
            caldavLink: 'Add a CalDAV account',
            microsoftTodoHint: 'With Outlook, you can tick off your to-dos as tasks in Microsoft To Do.',
            microsoftTodoLink: 'Connect Microsoft To Do',
            renew: 'New address',
            renewTitle: 'Create a new address?',
            renewDescription:
                'The current address will stop working. Wherever it’s subscribed to, you’ll need to add the new one.',
            renewConfirm: 'Create new address',
            feeds: {
                events: {
                    label: 'Events in a calendar',
                    description: 'The events from published recaps, as an address any calendar app can subscribe to.',
                },
            },
        },
        imap: {
            name: 'Mailbox',
            pickerName: 'Mailbox (IMAP)',
            description:
                'A mailbox over IMAP. Recap only reads emails; it doesn’t mark them as read or move them. A dedicated folder or address works best.',
            passwordHint: 'Leave empty to keep the saved password.',
            newPasswordHint: 'For Gmail, use an app password. Microsoft 365 isn’t supported yet.',
            folderHint: 'Usually INBOX, or a folder that a filter sorts the relevant emails into.',
            pickFolder: 'Choose a folder',
            foldersLoading: 'Loading folders …',
            ownNote:
                'What Recap reads from this mailbox belongs to the workspace: everyone who sees the client sees these emails too. Nobody sees your password.',
            ownFolderHint:
                'Best a folder or label of its own that a filter sorts the relevant emails into, so nothing private comes into the workspace.',
            providers: {
                placeholder: 'Choose a provider',
                gmail: 'Gmail',
                outlook: 'Outlook / Microsoft 365',
                other: 'Other provider',
            },
            gmail: {
                steps: {
                    verification: 'Turn on <page>2-Step Verification</page> in your Google account if it’s still off.',
                    appPassword:
                        'Create an app password at <page>myaccount.google.com/apppasswords</page>, for example named Recap.',
                    enter: 'Enter your Gmail address and the app password here. You can copy the spaces too.',
                },
                username: 'Gmail address',
                password: 'App password',
                passwordHint:
                    'If there’s no page for app passwords, 2-Step Verification is off or your Google Workspace admin doesn’t allow app passwords.',
                folderHint:
                    'In Gmail a folder is a label. It’s best to create a label like Recap and let a filter put the right emails there.',
            },
            fields: {
                provider: 'Provider',
                host: 'Server',
                port: 'Port',
                username: 'Username',
                password: 'Password',
                folder: 'Folder',
            },
            feeds: {
                email: {
                    label: 'Read emails',
                    description: 'Every email in a folder of the mailbox becomes a source.',
                },
            },
        },
    },
    userSettings: {
        details: 'Details',
        editTitle: 'Edit profile',
        sso: {
            title: 'Sign-in with Google',
            hint: 'You can sign in with your Google account {{email}}.',
            disconnect: 'Disconnect',
            disconnectTitle: 'Disconnect sign-in with Google?',
            disconnectConfirm: 'From then on, you sign in with a code we send to your email address.',
        },
        deleteAccount: {
            action: 'Delete account',
            hint: 'Deletes your account, your connections and your settings. What you wrote, acknowledged and published stays, without your name.',
            title: 'Delete account?',
            confirm:
                'Your account, connections and settings will be deleted, and you’ll leave all workspaces. Your comments, acknowledgements and published versions stay, shown as “Deleted user”. This can’t be undone.',
            email: 'Email address',
            emailHint: 'Enter your account’s email address to confirm.',
            submit: 'Delete account',
            ownsWorkspaces: 'You own {{names}}. Transfer or delete these workspaces before deleting your account.',
        },
        leaveClient: {
            action: 'Leave {{client}}',
            hint: 'You’ll no longer see the recaps for {{client}}.',
            title: 'Leave {{client}}?',
            confirm:
                'You’ll no longer see the recaps for {{client}} in {{workspace}}. You can only return with a new invitation.',
            submit: 'Leave',
        },
        fields: {
            firstName: 'First name',
            lastName: 'Last name',
            displayName: 'Display name',
            displayNameHelp: 'How others in your workspaces see you.',
            jobTitle: 'Job title',
            jobTitleHelp:
                'Optional, for example “Project lead”. Shown next to your name wherever a recap mentions you.',
            id: 'User ID',
            avatar: 'Photo',
        },
        avatar: {
            choose: 'Choose photo',
            hint: 'PNG, JPEG or WebP, up to 10 MB. It’s cropped to a square from the centre.',
        },
        email: {
            title: 'Email address',
            change: 'Change',
            enterCode: 'Enter code',
            current: 'Current address',
            pending: 'New address',
            pendingHint: 'Waiting to be verified',
            changeTitle: 'Change email address',
            changeDescription: 'We’ll send a code to the new address. Your current address stays until you enter it.',
            codeTitle: 'Enter code',
            submit: 'Verify',
        },
        appearance: {
            title: 'Appearance',
            general: 'Language and mode',
            language: 'Language',
            mode: 'Mode',
            modes: {
                system: 'Use system setting',
                light: 'Light',
                dark: 'Dark',
            },
            themes: {
                title: 'Colours',
                hint: 'How Recap looks for you. Only you see it this way, in every browser you’re signed in to.',
                classic: { name: 'Classic', description: 'Black, white and grey, the way Recap has always looked.' },
                terracotta: { name: 'Terracotta', description: 'Terracotta on warm paper white.' },
                graphite: { name: 'Graphite', description: 'Graphite and copper on warm stone.' },
                navy: { name: 'Navy', description: 'A dark navy blue on cool white.' },
                slate: { name: 'Slate', description: 'A muted blue-grey, the calmest of them.' },
                indigo: { name: 'Indigo', description: 'A deep indigo on warm stone.' },
            },
            layouts: {
                title: 'Layout',
                hint: 'How the sidebar, the top bar and the page sit together.',
                flat: { name: 'Flat', description: 'All one surface, separated only by lines.' },
                tinted_rail: { name: 'Tinted sidebar', description: 'The sidebar a shade darker.' },
                canvas: {
                    name: 'Canvas',
                    description: 'Light bars, the page a shade darker and light cards on it.',
                },
                inset: { name: 'Inset', description: 'The page as a sheet set into the sidebar’s surface.' },
                dark_frame: { name: 'Dark frame', description: 'Dark bars around the light page.' },
            },
            fonts: {
                title: 'Font for headings',
                sample: 'Good morning',
                inter: 'The font of the whole app, plain and clear.',
                fraunces: 'A soft, warm serif.',
                newsreader: 'A calm serif made for reading.',
                hanken_grotesk: 'A warm, simple sans serif.',
                bricolage_grotesque: 'A strong sans serif with character.',
            },
            ownColors: {
                title: 'Your own colours',
                hint: 'Change each colour on its own, for light and for dark. A change only affects that one colour.',
                light: 'Light',
                dark: 'Dark',
                darkFollows: 'Adjust dark colours automatically',
                groups: {
                    page: 'Page and cards',
                    text: 'Text',
                    primary: 'Main colour',
                    navigation: 'Navigation',
                    highlights: 'Highlights',
                },
                code: 'As code',
                codeHint:
                    'Your whole appearance: colours, layout, font and every colour in light and dark. Copy it to take it with you, or paste another in to use it.',
                codeInvalid:
                    'This isn’t valid code yet: theme, layout, headline_font, colors_light and colors_dark, each colour as #rrggbb. The last valid appearance still applies.',
                reset: 'Reset your own colours',
                colors: {
                    primary: 'Main colour',
                    on_primary: 'Text on it',
                    surface: 'Page',
                    surface_shaded: 'Cards',
                    surface_muted: 'Grey',
                    border: 'Lines',
                    text: 'Text',
                    text_muted: 'Grey text',
                    rail: 'Sidebar',
                    top: 'Top bar',
                    chrome_dark: 'Dark frame',
                    nav_hover: 'Hovered item',
                    nav_active: 'Active item',
                    nav_active_text: 'Icon on it',
                    today: 'Today',
                    today_text: 'Text on today',
                    eyebrow: 'Date above',
                    tab_line: 'Tab line',
                    bar_current: 'Today’s bar',
                    link: 'Links',
                },
            },
        },
        notifications: {
            title: 'Email notifications',
            hint: 'Choose what Recap emails you about: immediately, in the daily summary at 8:00, or never. To mute a single recap, use its menu.',
            sections: {
                recaps: 'Recaps',
                todos: 'To-dos',
                comments: 'Comments',
                acknowledgements: 'Acknowledgements',
                sources: 'Sources',
                connections: 'Connections',
            },
            deliveries: {
                immediately: 'Immediately',
                daily: 'Daily summary',
                never: 'Never',
            },
            kinds: {
                recap_created: 'A recap was created',
                recap_drafted: 'A draft of one of your recaps is ready or failed',
                version_published: 'A new version was published',
                todo_ticked: 'A to-do in one of your recaps was ticked off or reopened',
                todo_due: 'One of your to-dos is due tomorrow or was due yesterday',
                comment_added: 'Someone commented on your recaps or replied to you',
                acknowledgement_requested: 'You’re asked to acknowledge a recap, and reminders about it',
                recap_acknowledged: 'Someone acknowledged a version you published',
                source_received: 'A source arrived',
                source_failed: 'A source couldn’t be read',
                feed_failed: 'A connection stopped working',
            },
        },
    },
    clients: {
        tabs: {
            label: 'Sections',
            overview: 'Overview',
            team: 'Team',
            people: 'People',
            client: 'Client',
        },
        employeeCount_one: '{{count}} person',
        employeeCount_other: '{{count}} people',
        employeeCountInvited_one: '{{count}} person, {{invited}} invited',
        employeeCountInvited_other: '{{count}} people, {{invited}} of them invited',
        noEmployees: 'No people yet',
        filter: {
            search: 'Search',
            team: 'Team',
            all: 'All',
            mine: 'Mine',
        },
        noResults: {
            title: 'No clients found',
            description: 'No client matches your search and filters.',
            descriptionSearch: 'No client matches your search.',
            descriptionFilters: 'No client matches your filters.',
            searchEverywhere: 'Search all clients for “{{search}}”',
            showFiltered_one: 'Show all clients with this filter',
            showFiltered_other: 'Show all clients with these filters',
            showAll: 'Show all clients',
        },
        externalCompanies: {
            title: 'From {{provider}}',
            hint_one:
                'One customer from {{provider}} isn’t in Recap yet. Add it or link it to a client you already have.',
            hint_other:
                '{{count}} customers from {{provider}} aren’t in Recap yet. Add them or link them to clients you already have.',
            contactCount_one: 'One contact',
            contactCount_other: '{{count}} contacts',
            add: 'Add',
            open: 'Open in {{provider}}',
            dismiss: 'Hide',
            linkedWith: 'Linked to',
            inProvider: '{{name}} in {{provider}}',
            link: {
                action: 'Link to a client',
                title: 'Link {{name}}',
                hint: 'What comes from {{provider}} about this company then goes to this client.',
                submit: 'Link',
            },
            unlink: {
                action: 'Unlink',
                title: 'Unlink the company?',
                confirm:
                    'What comes from {{provider}} about {{name}} then no longer goes to {{client}} on its own. You can link the company again under “Clients”.',
                submit: 'Unlink',
            },
        },
        team: {
            title: 'Team',
            hint: 'Whoever is assigned to {{name}} gets its new sources and drafts.',
            assigned: {
                client: 'Assigned to the client:',
                project: 'Assigned to the project:',
            },
            scopeLabel: "Who's assigned",
            scopes: {
                workspace: 'everyone at {{workspace}}',
                client: 'everyone assigned to the client',
                chosen: 'only selected people',
            },
            details: {
                workspace_one: '{{count}} person at {{workspace}} and anyone who joins later',
                workspace_other: 'All {{count}} at {{workspace}} and anyone who joins later',
                client_zero: 'No one at {{workspace}} is assigned to {{client}} yet',
                client_one: '{{count}} person at {{workspace}} who is assigned to {{client}}',
                client_other: '{{count}} people at {{workspace}} who are assigned to {{client}}',
                chosen_one: '{{count}} of {{total}} at {{workspace}}',
                chosen_other: '{{count}} of {{total}} at {{workspace}}',
            },
            summary: {
                workspace: 'Whole team',
                client: 'The client’s team',
                none: 'No one on the team yet',
            },
            add: 'Add',
            remove: 'Remove from team',
            empty: {
                title: 'No one on the team yet',
                description: 'Use “Add” to add whoever works on {{name}}.',
            },
        },
        unfiledRecapCount_one: '{{count}} recap without a project',
        unfiledRecapCount_other: '{{count}} recaps without a project',
        unfiledSourceCount_one: '{{count}} source without a project',
        unfiledSourceCount_other: '{{count}} sources without a project',
        drafting: {
            label: 'Weekly draft',
            hint: 'At 6:00, Recap drafts a recap from any sources that are waiting.',
            never: 'Never',
            next: 'These sources will be drafted into a recap on {{weekday}}.',
        },
        hasChanges: 'A recap here has changed since you last opened it',
        star: {
            add: 'Add to favourites',
            remove: 'Remove from favourites',
        },
        emptyPage: {
            title: 'No clients yet',
            description: 'Each client has its projects, and they hold the recaps you share with that client.',
        },
        new: 'Create client',
        newTitle: 'New client',
        editTitle: 'Edit client',
        field: 'Client',
        none: 'No client',
        fields: {
            name: 'Name',
            slug: 'Short name',
            slugHint: 'Appears in the page’s address: <path />',
            id: 'Client ID',
            address: 'Address',
            description: 'Description',
            logo: 'Logo',
        },
        logo: {
            choose: 'Choose logo',
            hint: 'PNG, JPEG or WebP, up to 2 MB.',
        },
        sections: {
            projects: 'Projects',
            unfiledSources: 'Sources without a project',
            unfiledRecaps: 'Recaps without a project',
            details: 'Details',
        },
        access: {
            sharedWith: 'Shared with',
            toClient: 'Go to client',
            title: 'People at {{client}}',
            hint: 'They read the published recaps of {{project}} and acknowledge them. They never see your team’s drafts, sources or comments.',
            sentence: 'Who reads along:',
            label: 'Who reads along',
            all: 'everyone at {{client}}',
            chosen: 'only selected people',
            details: {
                all_one: '{{count}} person at {{client}} and anyone invited later',
                all_other: 'All {{count}} at {{client}} and anyone invited later',
                chosen_one: '{{count}} of {{total}} at {{client}}',
                chosen_other: '{{count}} of {{total}} at {{client}}',
            },
            invite: 'Invite someone from {{client}}',
            remove: 'Stop sharing with this person',
            empty: {
                title: 'No one from {{client}} reads along yet',
                description: 'Use “Add” to add whoever should read the recaps.',
            },
            noPeople: '{{client}} has no people yet',
            noPeopleHint: 'Invite whoever should read and acknowledge the recaps.',
            choose: 'Choose a client',
            otherClient: 'Change client',
            removeClient: 'Remove',
            newClient: 'Create a new client',
            shareTitle: 'Share with a client',
            shareHint:
                'Share {{project}} with a client so their people can read and acknowledge the recaps. Without a client, it stays an internal project for your team.',
            peopleOfClient: 'The client’s people',
            peopleOfClientHint: 'Choose here who at the client reads along: everyone or only selected people.',
            noClientHint: 'Once you choose a client, you decide here who of them reads the recaps.',
            move: {
                title: 'Share with {{client}}?',
                confirm:
                    '{{project}} moves to {{to}} with its recaps and sources. The people at {{from}} will no longer read it or be asked to acknowledge its recaps.',
                submit: 'Share',
            },
            takeAway: {
                title: 'Remove {{client}}?',
                confirm:
                    'The people at {{client}} will no longer read {{project}} or be asked to acknowledge its recaps. The project is then only for your team.',
                submit: 'Remove',
            },
        },
        employees: {
            title: 'People at {{client}}',
            hint: 'They read the published recaps of {{client}} and acknowledge them. Who reads which project is set on the project, in the “Client” tab.',
            invitedFor: 'Invited for {{projects}}',
            invite: {
                action: 'Invite someone',
                title: 'Invite someone from {{name}}',
                hint: 'We’ll email an invitation to this address. Only someone signed in with this address can join.',
            },
            contacts: {
                title: 'Contacts from {{provider}}',
                invite: 'Invite',
            },
            link: {
                none: 'Anyone with the link can join with their own account.',
            },
            projects: {
                all: 'Reads all projects',
                some: 'Reads {{projects}}',
                empty: 'Doesn’t read any project yet',
            },
            remove: {
                action: 'Remove',
                title: 'Remove this person?',
                confirm: '{{name}} will no longer be one of the people at {{client}}.',
                submit: 'Remove',
            },
        },
        allSources_one: 'View source',
        allSources_other: 'View all {{count}}',
        allRecaps_one: 'View recap',
        allRecaps_other: 'View all {{count}}',
        noUnfiledSources: {
            description: 'Sources from {{client}} that aren’t assigned to a project yet show up here.',
        },
        unfiledNeedProject: 'Create a project to assign this client’s sources to it.',
        delete: {
            action: 'Delete client',
            hint: 'Deletes all its projects, employees and invitations. Its sources stay, without a client.',
            title: 'Delete client?',
            confirm:
                '{{name}} will be deleted along with all its projects. Its sources stay, without a client. This can’t be undone.',
            submit: 'Delete',
            blockedTitle: 'Client can’t be deleted',
            blocked: 'The projects of {{name}} have recaps. Move or delete them first – they record what was agreed.',
        },
        projects: {
            count_one: '{{count}} project',
            count_other: '{{count}} projects',
            empty: 'No projects yet.',
            new: 'Create project',
            newTitle: 'New project',
            newHint:
                'A project holds the sources and recaps of a piece of work. With a client, their people read the recaps; without one, it stays an internal project for your team.',
            withoutClient: 'No client',
            placeholder: {
                title: 'No project',
                create: 'Create the first project',
            },
            noResults: {
                title: 'No projects found',
                description: 'No project matches your search and filters.',
                descriptionSearch: 'No project matches your search.',
                descriptionFilters: 'No project matches your filters.',
                searchEverywhere: 'Search all projects for “{{search}}”',
                showFiltered_one: 'Show all projects with this filter',
                showFiltered_other: 'Show all projects with these filters',
                showAll: 'Show all projects',
            },
            page: {
                empty: {
                    title: 'No projects yet',
                    description:
                        'A project holds the sources and recaps of a piece of work, for a client or just for your team.',
                },
            },
            editTitle: 'Edit project',
            field: 'Project',
            none: 'No project',
            pick: 'Choose a project',
            waitingSources_one: '{{count}} new source',
            waitingSources_other: '{{count}} new sources',
            noRecaps: 'No recaps yet',
            lastRecap: 'last on {{date}}',
            weeklyDraft: 'Drafted every {{weekday}}',
            newSources: 'New sources',
            newSourcesEmpty: {
                description: 'Sources for “{{project}}” that aren’t in a recap yet show up here.',
            },
            emptySection: {
                description: 'Projects hold the recaps for {{client}}, for example one per job.',
            },
            recapsEmpty: {
                description: 'Upload files for this project, and they become a recap.',
            },
            notPublic: 'Not public',
            publicLink: {
                title: 'Public link',
                hint: 'Anyone with the link can read the published recaps of {{project}} without an account. They can’t see comments, acknowledgements or older versions.',
                create: 'Create link',
                renew: 'Create new link',
                turnOff: 'Turn off link',
            },
            fields: {
                id: 'Project ID',
                name: 'Name',
                client: 'Client',
            },
            delete: {
                action: 'Delete project',
                hint: 'Its sources stay, without a project.',
                button: 'Delete',
                title: 'Delete project?',
                confirm: '{{name}} will be deleted. This can’t be undone.',
                submit: 'Delete',
                blockedTitle: 'Project can’t be deleted',
                blocked: '{{name}} has recaps. Move or delete them first – they record what was agreed.',
            },
        },
    },
    employee: {
        team: {
            title: 'Your team at {{workspace}}',
        },
        emptySection: {
            title: 'No recaps yet',
            project: 'Once a recap for this project is published, you’ll find it here.',
            all: 'Once a recap is published for you, you’ll find it here.',
            notifications: 'Set up notifications',
        },
        home: {
            intro: 'Here, {{workspace}} records what was discussed and decided with {{client}}.',
            waitingTitle: 'Waiting for your acknowledgement',
            allRecaps_one: 'View recap',
            allRecaps_other: 'View all {{count}}',
        },
        acknowledge: {
            title: 'Please acknowledge version {{number}}',
            description: 'By acknowledging, you confirm that the recap reflects what was agreed.',
            changedSince: 'Changed since you acknowledged version {{number}}:',
            button: 'Acknowledge',
            done: 'You acknowledged this on {{date}}',
        },
        recap: {
            published: 'Published on {{date}}',
            viewLatest: 'View latest version',
            olderVersion: 'The latest version is {{number}}.',
        },
    },
    home: {
        greeting: {
            morning: 'Good morning, {{name}}',
            day: 'Hello, {{name}}',
            evening: 'Good evening, {{name}}',
        },
        overdueSince: 'overdue since {{date}}',
        allDay: 'all day',
        summary: {
            overdue_one: '{{count}} to-do is overdue',
            overdue_other: '{{count}} to-dos are overdue',
            dueTodayOne: '“{{title}}” is due today',
            dueToday_one: '{{count}} to-do is due today',
            dueToday_other: '{{count}} to-dos are due today',
            owed: '{{name}} owes you “{{title}}” by {{date}}',
            owedLate: '{{name}} is late with “{{title}}”',
            event: '“{{title}}” is {{when}}',
            atTime: 'today at {{time}}',
            onDay: 'on {{date}}',
            calm: 'Nothing is due today. A good moment to go through new sources.',
        },
        stats: {
            open: 'Open to-dos',
            overdue_one: '{{count}} overdue',
            overdue_other: '{{count}} overdue',
            nothingOverdue: 'nothing overdue',
            done: 'Done this week',
            doneDetail: 'to-dos since Monday',
            doneTrend: 'To-dos done in the last 7 days: {{values}}',
            received: 'New sources',
            receivedDetail: 'in the last 7 days',
            receivedTrend: 'Sources received in the last 7 days: {{values}}',
            events: 'Events this week',
            nextEvent: 'next on {{date}}',
            noEvents: 'none left this week',
        },
        week: {
            title: 'This week',
            nextUp: 'Nothing on this week. Next up:',
            nothingNext: 'No more appointments',
            nothingNextHint: 'Appointments and due to-dos from the recaps show up here once there are any.',
            itemCount_one: '{{count}} item',
            itemCount_other: '{{count}} items',
        },
        myTodos: {
            title: 'Your to-dos',
            done: 'Done',
            undo: 'Undo',
            allDone: 'All done',
            allDoneHint: 'You have no open to-dos.',
        },
        news: {
            recapAcknowledged: '{{user}} acknowledged version {{number}} of “{{recap}}”',
            acknowledgerRemoved: '{{name}} is no longer asked to acknowledge “{{recap}}”',
            recapProcessedAgain: '{{user}} redrafted “{{recap}}”',
            recapSourcesChanged: '{{user}} changed the sources of “{{recap}}”',
            draftDiscarded: '{{user}} discarded the draft of “{{recap}}”',
            versionRestored: '{{user}} restored version {{number}} of “{{recap}}”',
            recapMoved: '{{user}} moved “{{recap}}”',
            sourceMoved: '{{user}} moved a source',
            publicLinkCreated: '{{user}} created a public link to “{{recap}}”',
            publicLinkRemoved: '{{user}} removed the public link to “{{recap}}”',
            more_one: '{{count}} more in Activity',
            more_other: '{{count}} more in Activity',
            all: 'All activity',
            sources_one: 'New source from {{client}}',
            sources_other: '{{count}} new sources from {{client}}',
            sourcesWithoutClient_one: 'New source without a client',
            sourcesWithoutClient_other: '{{count}} new sources without a client',
            sourcesOfProject_one: 'New source in “{{project}}”',
            sourcesOfProject_other: '{{count}} new sources in “{{project}}”',
            sourceFailed: 'A source couldn’t be read',
            sourceUpdated: '{{user}} updated a source',
            sourceProcessedAgain: '{{user}} reprocessed a source',
            sourceChanged: 'A newer version of a source is available',
            recapCreated: '{{user}} created “{{recap}}”',
            recapDrafted: 'The draft of “{{recap}}” is ready',
            recapFailed: 'Drafting “{{recap}}” failed',
            versionPublished: '{{user}} published version {{number}} of “{{recap}}”',
            todoDone: '{{user}} ticked off “{{title}}”',
            todoUndone: '{{user}} reopened “{{title}}”',
            todos: '{{user}} ticked off {{count}} to-dos in “{{recap}}”',
            comments_one: 'New comment on “{{recap}}”',
            comments_other: '{{count}} new comments on “{{recap}}”',
            sourceComments_one: 'New comment on a source',
            sourceComments_other: '{{count}} new comments on sources',
        },
        clients: {
            title: 'Your clients',
            todosTitle_one: '{{count}} open to-do',
            todosTitle_other: '{{count}} open to-dos',
            noTodos: 'No open to-dos',
            moreTodos_one: '{{count}} more on the client’s page',
            moreTodos_other: '{{count}} more on the client’s page',
            eventsTitle: 'Upcoming events',
            health: {
                overdue: 'Overdue',
                needs_recap: 'New sources',
            },
            counts: {
                sources_one: '{{count}} source waiting for a recap',
                sources_other: '{{count}} sources waiting for a recap',
                todos_one: '{{count}} open to-do',
                todos_other: '{{count}} open to-dos',
                events_one: '{{count}} upcoming event',
                events_other: '{{count}} upcoming events',
            },
            noEvent: 'No events scheduled',
            createRecap_one: 'Create recap from {{count}} source',
            createRecap_other: 'Create recap from {{count}} sources',
            waitingSources_one: '{{count}} source is waiting for a recap.',
            waitingSources_other: '{{count}} sources are waiting for a recap.',
            toggle: 'Show or hide {{name}}',
        },
        projects: {
            title: 'Internal projects',
            moreTodos_one: '{{count}} more on the project’s page',
            moreTodos_other: '{{count}} more on the project’s page',
        },
    },
    activities: {
        markAllSeen: 'Mark all as seen',
        automatic: {
            on_arrival: 'Recap created automatically from a new source',
            weekly: 'Weekly recap created automatically',
        },
        newForYou: 'New for you',
        none: 'Nothing has happened yet',
        emptyPage: {
            title: 'Nothing has happened yet',
            description:
                'This is where you see what happens in the recaps: new drafts, comments and published versions.',
            toSources: 'Go to Sources',
        },
        deletedRecap: 'Deleted recap',
        deletedSource: 'Deleted source',
        noClient: 'no client',
        someone: 'Someone',
        you: 'You',
        group: { expand: 'Show individually', collapse: 'Group' },
        details: { expand: 'Show details', collapse: 'Hide details' },
        published: 'published version {{number}}',
        published_you: 'published version {{number}}',
        acknowledgementRequested: 'New acknowledgement requested',
        changes: {
            kinds: {
                source_file: 'File',
                person: 'Person',
                topic: 'Topic',
                decision: 'Decision',
                todo: 'To-do',
                event: 'Event',
            },
            more_one: '{{count}} more change',
            more_other: '{{count}} more changes',
        },
        drafts: {
            drafted: 'Draft created',
            draftedAgain_one: 'Redrafted',
            draftedAgain_other: 'Redrafted {{count}} times',
            createdAndDrafted_one: 'created a draft',
            createdAndDrafted_you_one: 'created a draft',
            createdAndDrafted_other: 'created {{count}} drafts',
            createdAndDrafted_you_other: 'created {{count}} drafts',
        },
        todos: {
            done: 'ticked off “{{title}}”',
            done_you: 'ticked off “{{title}}”',
            itemDone: 'Ticked off “{{title}}”',
            entryDone: 'Ticked off',
            entryUndone: 'Reopened',
            itemUndone: 'Reopened “{{title}}”',
            undone: 'reopened “{{title}}”',
            undone_you: 'reopened “{{title}}”',
            doneAndUndone: 'ticked off “{{title}}” and reopened it',
            doneAndUndone_you: 'ticked off “{{title}}” and reopened it',
            undoneAndDone: 'reopened “{{title}}” and ticked it off again',
            undoneAndDone_you: 'reopened “{{title}}” and ticked it off again',
            backAndForthDone: 'ticked “{{title}}” on and off a few times and finally ticked it off',
            backAndForthDone_you: 'ticked “{{title}}” on and off a few times and finally ticked it off',
            backAndForthUndone: 'ticked “{{title}}” on and off a few times and left it open',
            backAndForthUndone_you: 'ticked “{{title}}” on and off a few times and left it open',
            severalDone_one: 'ticked off {{count}} to-do',
            severalDone_you_one: 'ticked off {{count}} to-do',
            severalDone_other: 'ticked off {{count}} to-dos',
            severalDone_you_other: 'ticked off {{count}} to-dos',
            severalUndone_one: 'reopened {{count}} to-do',
            severalUndone_you_one: 'reopened {{count}} to-do',
            severalUndone_other: 'reopened {{count}} to-dos',
            severalUndone_you_other: 'reopened {{count}} to-dos',
            severalMixed_one: 'ticked off {{count}} to-do and reopened {{undone}}',
            severalMixed_you_one: 'ticked off {{count}} to-do and reopened {{undone}}',
            severalMixed_other: 'ticked off {{count}} to-dos and reopened {{undone}}',
            severalMixed_you_other: 'ticked off {{count}} to-dos and reopened {{undone}}',
        },
        sources: {
            added_one: 'added {{count}} source',
            added_you_one: 'added {{count}} source',
            added_other: 'added {{count}} sources',
            added_you_other: 'added {{count}} sources',
            synced_one: '{{count}} email arrived',
            synced_other: '{{count}} emails arrived',
        },
        commentOn: {
            topic: 'On the topic “{{title}}”',
            decision: 'On the decision “{{title}}”',
            todo: 'On the to-do “{{title}}”',
            event: 'On the event “{{title}}”',
        },
        kinds: {
            source_received: 'Source added',
            source_failed: 'Processing failed',
            source_updated: 'Updated the source',
            source_processed_again: 'Reprocessed the source',
            source_changed: 'Newer version available',
            recap_created: 'Recap created',
            recap_drafted: 'Draft created',
            recap_failed: 'Drafting failed',
            version_published: 'Version {{number}} published',
            todo_done: 'Done: {{title}}',
            todo_undone: 'Reopened: {{title}}',
            item_superseded: 'Replaced by a later recap: {{title}}',
            comment_added: 'Commented',
            recap_acknowledged: 'Acknowledged version {{number}}',
            acknowledger_removed: '{{name}} lost access to the project and is no longer asked to acknowledge',
            recap_processed_again: 'Redrafted',
            recap_sources_changed: 'Changed the sources',
            draft_discarded: 'Discarded the draft',
            version_restored: 'Restored version {{number}}',
            recap_moved: 'Moved to {{place}}',
            source_moved: 'Moved to {{place}}',
            public_link_created: 'Created a public link',
            public_link_removed: 'Removed the public link',
            recap_deleted: 'Deleted the recap',
            source_deleted: 'Deleted the source',
            client_created: 'Client created',
            client_renamed: 'Renamed from “{{title}}”',
            client_deleted: 'Client deleted',
            project_created: 'Project created',
            project_renamed: 'Renamed from “{{title}}”',
            project_deleted: 'Project deleted',
            project_client_changed: 'Client changed',
        },
    },
    comments: {
        isNew: 'New since you last opened the recap',
        title: 'Comments',
        show_one: '{{count}} comment',
        show_other: '{{count}} comments',
        add: 'Comment',
        empty: 'No comments yet',
        placeholder: 'Write a comment …',
        submit: 'Comment',
        onDraftNote: 'You’re commenting on the draft of version {{number}}. Only members can see this comment.',
        edited: 'edited',
        onVersion: 'On version {{number}}',
        onDraft: 'On the draft of version {{number}}',
        onItem: {
            topic: 'On the topic “{{title}}”',
            decision: 'On the decision “{{title}}”',
            todo: 'On the to-do “{{title}}”',
            event: 'On the event “{{title}}”',
        },
        onUnknownItem: {
            topic: 'On a topic',
            decision: 'On a decision',
            todo: 'On a to-do',
            event: 'On an event',
        },
        deletedUser: 'Deleted user',
        reply: 'Reply',
        replyingTo: 'Replying to {{name}}',
        stopReplying: 'Cancel reply',
        onComment: 'On the comment “{{text}}”',
        onDeletedComment: 'On a deleted comment',
        delete: {
            title: 'Delete comment?',
            confirm: 'This can’t be undone.',
            submit: 'Delete',
        },
    },
    recaps: {
        hasChanges: 'Changed since you last opened it',
        itemChanged: 'New or changed since you last opened the recap',
        activity: {
            title: 'Activity',
            onlyComments: 'Comments only',
            order: 'Order',
            newest: 'Newest first',
            oldest: 'Oldest first',
        },
        resultCount_one: '{{count}} recap',
        resultCount_other: '{{count}} recaps',
        emptyPage: {
            title: 'No recaps yet',
            description:
                'Upload emails, meeting notes or documents, and they become a recap. You can also <create>write a recap yourself</create>.',
        },
        upload: {
            description: 'Choose the client and project the files belong to. They then become a recap.',
        },
        noResults: {
            title: 'No recaps found',
            description: 'No recap matches your search and filters.',
            descriptionSearch: 'No recap matches your search.',
            descriptionFilters: 'No recap matches your filters.',
            searchEverywhere: 'Search all recaps for “{{search}}”',
            showFiltered_one: 'Show all recaps with this filter',
            showFiltered_other: 'Show all recaps with these filters',
            showAll: 'Show all recaps',
        },
        draftsTitle: 'Drafts',
        processingTitle: 'Processing …',
        untitled: 'Untitled',
        failedTitle: 'Processing failed',
        failed: 'You can restart it under “More actions”.',
        noSummary: 'No summary found',
        processAgain: 'Process again',
        publicLink: {
            hint: 'Anyone with the link can read the published version of this recap without an account, but nothing else in the project. They can’t see comments, acknowledgements or older versions.',
            notPublished: 'Only a published recap can be shared. Once it’s published, you can create a link here.',
        },
        mute: {
            mute: 'Mute notifications',
            unmute: 'Unmute notifications',
            muted: 'Muted: you won’t get emails about this recap.',
        },
        editing: {
            title: 'You’re editing version {{number}}',
            published:
                'Version {{number}} was last published on {{date}}. Only members can see your changes until you publish them.',
            notPublished: 'Not published yet. Only members can see it until you publish it.',
            close: 'Stop editing',
        },
        sections: {
            summary: 'Summary',
            topics: 'Topics',
            persons: 'People',
            sources: 'Sources',
            decisions: 'Decisions',
            todos: 'To-dos',
            events: 'Events',
        },
        items: {
            topic: 'Topic',
            decision: 'Decision',
            todo: 'To-do',
            todoDone: 'To-do, done',
            supersedes: 'Replaces “{{title}}” from <recap>“{{recap}}”</recap>',
            supersedesFrom: 'Replaces an item from <recap>“{{recap}}”</recap>',
            openExternal: 'Open in {{provider}}',
            openExternalUnnamed: 'Open in the tool',
            supersededBy: 'Replaced by “{{title}}” in <recap>“{{recap}}”</recap>',
            supersededIn: 'Replaced in <recap>“{{recap}}”</recap>',
            event: 'Event',
        },
        todoDue: 'Due {{date}}',
        allDay: 'All day',
        linkedPerson: 'Linked to an account',
        counts: {
            persons_one: '{{count}} person',
            persons_other: '{{count}} people',
            topics_one: '{{count}} topic',
            topics_other: '{{count}} topics',
            decisions_one: '{{count}} decision',
            decisions_other: '{{count}} decisions',
            todos_one: '{{count}} to-do',
            todos_other: '{{count}} to-dos',
            openForYou_one: '{{count}} of them open for you',
            openForYou_other: '{{count}} of them open for you',
            events_one: '{{count}} event',
            events_other: '{{count}} events',
        },
        persons: {
            participants: 'Participants',
            mentioned: 'Mentioned',
        },
        citations: {
            show_one: '{{count}} citation',
            show_other: '{{count}} citations',
            title: 'Citations',
            sourceDeleted: 'Source deleted',
        },
        create: {
            button: 'Create recap',
            title: 'Create recap',
            description: 'The recap is drafted from these files. Files you untick are left out.',
            submit: 'Create',
        },
        sourceCount_one: '{{count}} source',
        sourceCount_other: '{{count}} sources',
        new: {
            button: 'Create recap',
            title: 'Create recap',
            description:
                'A recap you fill in yourself. To have one drafted from sources, select them on the Sources page.',
        },
        project: {
            button: 'Change client and project',
            title: 'Client and project',
            description: 'Which client and project does this recap belong to? Its content stays the same.',
        },
        draftOfNextVersionTitle: 'Draft of version {{number}}',
        draftOfNextVersion: 'Only members can see this. Version {{published}} is published.',
        notPublishedTitle: 'Draft',
        notPublished: 'Not published yet. Only members can see it.',
        hasDraft: {
            title: 'There’s a draft of version {{number}}',
            description: 'Only members can see it until it’s published.',
        },
        drafting: {
            title: 'Version {{number}} is being drafted',
            description: 'Until the draft is ready, you’ll see the published version here.',
        },
        publishedVersion: 'Version {{number}} · published {{date}}',
        draftedAt: 'Drafted {{date}}',
        status: {
            draft: 'Draft',
            version: 'Version {{number}}',
            versionPublished: 'Version {{number}} published',
        },
        // What a row's icon says (RecapStateIcon).
        state: {
            legend: 'What the icons mean',
            processing: 'Being drafted',
            failed: 'Failed',
            draft: 'Draft',
            newDraft: 'New draft',
            waiting: 'Waiting for acknowledgements',
            published: 'Published',
        },
        fields: {
            title: 'Title',
            summary: 'Summary',
            description: 'Description',
            name: 'Name',
            email: 'Email',
            company: 'Company',
            jobTitle: 'Job title',
            user: 'Account',
            userHelp: 'Once linked, the person is shown with the account’s name and email.',
            noUser: 'Not linked',
            members: 'Team',
            employeesOf: 'People at {{client}}',
            isParticipant: 'Took part',
            person: 'Person',
            noPerson: 'Nobody',
            supersedes: 'Replaces',
            noSupersedes: 'Nothing',
            supersedesHelp: {
                todo: 'An open to-do from an earlier recap that this one takes up again. Once this recap is published, it disappears from every list.',
                event: 'An event from an earlier recap that this one moves. Once this recap is published, it disappears from every list and calendar.',
            },
            dueDate: 'Due on',
            date: 'Date',
            time: 'Time',
            timeHelp: 'Leave empty for an all-day event',
            topic: 'Topic',
        },
        edit: {
            add: 'Add',
            addTopic: 'Topic',
            addPerson: 'Person',
            addFirstTopic: 'Add topic',
            addFirstPerson: 'Add person',
            addDecision: 'Add decision',
            addTodo: 'Add to-do',
            addEvent: 'Add event',
            noTopics: 'No topics yet',
            noPersons: 'No people yet',
            moveUp: 'Move up',
            moveDown: 'Move down',
            markDone: 'Mark as done',
            markNotDone: 'Mark as open',
            titles: {
                recap: 'Edit recap',
                person: 'Edit person',
                topic: 'Edit topic',
                decision: 'Edit decision',
                todo: 'Edit to-do',
                event: 'Edit event',
            },
            newTitles: {
                person: 'New person',
                topic: 'New topic',
                decision: 'New decision',
                todo: 'New to-do',
                event: 'New event',
            },
            deleteTitles: {
                person: 'Remove person?',
                topic: 'Delete topic?',
                decision: 'Delete decision?',
                todo: 'Delete to-do?',
                event: 'Delete event?',
            },
            deleteDescriptions: {
                person: '“{{name}}” will be removed from the recap. Their to-dos stay, without a person assigned.',
                topic: '“{{name}}” will be deleted along with its decisions, to-dos and events.',
                decision: '“{{name}}” will be deleted.',
                todo: '“{{name}}” will be deleted.',
                event: '“{{name}}” will be deleted.',
            },
        },
        publish: {
            button: 'Publish',
            title: 'Publish version {{number}}?',
            descriptionFirst:
                'The recap becomes version 1. Any changes you make afterwards go into a draft of the next version.',
            descriptionNext: 'The draft becomes the next version and replaces the published one.',
            changesSince: 'Changed since version {{number}}',
            requireAcknowledgement: 'Ask for acknowledgement again',
            submit: 'Publish',
        },
        discard: {
            button: 'Discard draft',
            title: 'Discard draft?',
            description:
                'All changes since version {{number}} will be lost, and the recap goes back to how it was published. To-dos that were ticked off stay done.',
            submit: 'Discard',
        },
        restore: {
            button: 'Restore this version',
            title: 'Restore version {{number}}?',
            makesDraft: 'Its content becomes the draft of version {{next}}, which you can edit and publish.',
            replacesDraft:
                'Its content replaces the draft of version {{next}}, and all changes in that draft will be lost. To-dos that were ticked off stay done.',
        },
        versions: {
            button: 'Versions',
            title: 'Versions',
            number: 'Version {{number}}',
            acknowledgementRequired: 'Acknowledgement requested',
            view: 'View',
            first: 'The first published version.',
            viewingTitle: 'You’re viewing version {{number}}',
            viewing: 'Published on {{date}}.',
            viewDraft: 'View draft',
            viewCurrent: 'View current version',
            viewLatest: 'View latest version',
            viewPublished: 'View published version',
        },
        sourceFile: {
            attachmentOf: 'Attachment of “{{subject}}”',
            attachment: 'Email attachment',
            from: 'From',
            to: 'To',
            cc: 'Cc',
            more: '+{{count}}',
        },
        editSources: {
            button: 'Edit',
            edit: {
                title: 'Edit sources',
                submit: 'Save and redraft',
            },
            process: {
                title: 'Process again',
                submit: 'Process again',
            },
            description: 'The recap is redrafted from what’s ticked.',
            new: 'New',
            createdAt: 'Recap created on {{date}}',
            noSources: 'No source is waiting for a recap.',
            keptTitle: 'Your changes stay',
            kept: 'The new draft builds on the recap as it is: what was changed by hand stays, and what the sources say anew comes in. For a completely new draft, you can delete the recap and create it again from the sources.',
            nextVersion: 'The result is a draft of the next version.',
            empty: {
                title: 'No sources yet',
                description: 'Add some to draft the recap from them.',
            },
        },
        changes: {
            none: 'Nothing changed.',
            empty: 'empty',
            fields: {
                title: 'Title',
                summary: 'Summary',
                description: 'Description',
                name: 'Name',
                email: 'Email',
                company: 'Company',
                job_title: 'Job title',
                is_participant: 'Took part',
                user: 'Account',
                topic: 'Topic',
                person: 'Person',
                due_date: 'Due on',
                date: 'Date',
                time: 'Time',
            },
        },
        filters: {
            search: 'Search',
            status: 'Status',
            allStatuses: 'All statuses',
            client: 'Client',
            allClients: 'All clients',
            withoutClient: 'No client',
            withoutProject: 'No project',
            project: 'Project',
            allProjects: 'All projects',
            publication: 'Publication',
            allPublications: 'All',
            publications: {
                draft: 'Drafts',
                published: 'Published',
            },
            acknowledgement: 'Acknowledgement',
            allAcknowledgements: 'All',
            acknowledgements: {
                waiting: 'Waiting for acknowledgement',
                acknowledged: 'Acknowledged',
            },
        },
        acknowledgers: {
            title: 'Acknowledgements',
            who: 'Who needs to acknowledge it?',
            withoutClient: 'This recap has no client, so nobody can be asked to acknowledge it.',
            noEmployees: '{{client}} has no people yet.',
            noReaders: 'No one from {{client}} reads this project yet.',
            chooseReaders: 'Choose who reads along',
            none: 'Nobody has been asked to acknowledge this version',
            acknowledged: 'Acknowledged version {{number}} · {{date}}',
            waiting: 'Waiting',
            waitingSince: 'Acknowledged version {{acknowledged}}, waiting for version {{number}}',
            editTitle: 'Who needs to acknowledge it?',
            editDescription:
                'Applies to the published version {{number}} without creating a new version. Existing acknowledgements stay.',
            count: '{{acknowledged}} of {{count}} acknowledged',
            all: 'Acknowledged',
            asked: 'Asked to acknowledge: {{names}}',
            acknowledgedBy: 'Acknowledged by {{names}}',
            notAccepted: "Hasn't accepted the invitation yet",
            invitedHint: "Invited people get an email and can acknowledge once they've accepted their invitation.",
        },
        delete: {
            button: 'Delete',
            title: 'Delete recap?',
            confirm: '“{{name}}” will be deleted. Its sources stay. This can’t be undone.',
            submit: 'Delete',
            blocked: 'This recap can’t be deleted.',
        },
    },
    sources: {
        isNew: 'New since your last visit',
        uploadDialog: {
            title_one: 'Upload file',
            title_other: 'Upload {{count}} files',
            description: 'Choose the client and project the sources belong to. You can also change this later.',
            submit: 'Upload',
        },
        pickDialog: {
            title_one: 'One file from {{provider}}',
            title_other: '{{count}} files from {{provider}}',
            emailTitle_one: 'One email from {{provider}}',
            emailTitle_other: '{{count}} emails from {{provider}}',
            documentTitle_one: 'One page from {{provider}}',
            documentTitle_other: '{{count}} pages from {{provider}}',
            taskTitle_one: 'One card from {{provider}}',
            taskTitle_other: '{{count}} cards from {{provider}}',
            submit: 'Add',
            reading: 'Reading …',
            failedTitle: 'The files couldn’t be added',
        },
        pickItems: {
            title: {
                email: 'Choose emails',
                document: 'Choose documents',
                task: 'Choose cards',
            },
            place: {
                email: 'Folder',
                document: 'Section',
                task: 'Board or list',
            },
            search: {
                email: 'Search emails',
                document: 'Search documents',
                task: 'Search cards',
            },
            shown: 'Which',
            new: 'New',
            all: 'All',
            next: 'Next',
            limit: 'At most {{count}} at once',
            added: '{{title}} (added already)',
            attachments: 'With attachments',
            noSubject: '(No subject)',
            due: 'due {{date}}',
            allAdded: 'Everything here has been added already.',
            showAll: 'Show all',
            loading: 'Loading …',
            empty: 'There’s nothing here.',
            noResults: 'Nothing found for “{{search}}”.',
            clearSearch: 'Clear search',
        },
        textDialog: {
            title: 'Add source from text',
            description:
                'Paste notes, a message or a transcript. Choose the client and project the text belongs to – you can also change this later.',
            titleLabel: 'Title',
            defaultTitle: 'Note',
            text: 'Text',
            submit: 'Add',
        },
        linkDialog: {
            title: 'Add source from link',
            description:
                'Recap reads the link and turns it into a source. Choose the client and project it belongs to – you can also change this later.',
            url: 'Link',
            shareInGoogleDrive: 'Share in Google Drive',
            notShared: 'Recap doesn’t have access to this file yet. Share it in {{name}} so it can become a source.',
            readThrough: 'Read via {{name}}',
            notConnected: 'There’s no connection to {{name}} yet. Connect it in the settings to read this link.',
            submit: 'Add',
            reading: 'Reading …',
            existing: 'This source already exists.',
        },
        update: {
            title: 'Changed in {{provider}}',
            description: 'This has been changed there since the source arrived. Apply the update to read it again.',
            service: 'the original',
            take: 'Apply update',
            pill: 'Update available',
            confirmTitle: 'Apply update?',
            confirm: 'The source will be replaced with the latest version and read again.',
            confirmWithRecaps_one:
                'The source will be replaced with the latest version and read again. Then the recap {{recaps}} will be redrafted, and any manual edits to its draft will be replaced. Published versions stay until the next one is published.',
            confirmWithRecaps_other:
                'The source will be replaced with the latest version and read again. Then the recaps {{recaps}} will be redrafted, and any manual edits to their drafts will be replaced. Published versions stay until the next one is published.',
        },
        via: 'via {{name}}',
        fromMailboxOf: 'from {{name}}’s mailbox',
        origin: {
            upload: 'via upload',
            email: 'via email',
            mobile: 'via phone',
            document: 'via document',
            link: 'via link',
            ticket: 'via ticket',
            calendar_event: 'from a calendar',
            meeting: 'from a meeting',
            conversation: 'from a conversation',
        },
        openIn: 'Open in {{name}}',
        resultCount_one: '{{count}} source',
        resultCount_other: '{{count}} sources',
        emptyPage: {
            title: 'No sources yet',
            description:
                'Recaps are drafted from sources. Upload files or <connect>connect a mailbox</connect>, and emails come in by themselves.',
        },
        noResults: {
            title: 'No sources found',
            description: 'No source matches your search and filters.',
            descriptionSearch: 'No source matches your search.',
            descriptionFilters: 'No source matches your filters.',
            searchEverywhere: 'Search all sources for “{{search}}”',
            showFiltered_one: 'Show all sources with this filter',
            showFiltered_other: 'Show all sources with these filters',
            showAll: 'Show all sources',
        },
        processingTitle: 'Processing',
        unfiledTitle: 'Not in a recap yet',
        untitled: 'Untitled',
        failedTitle: 'Processing failed',
        failed: 'You can restart it under “More actions”.',
        processAgain: 'Process again',
        fileCount_one: '{{count}} file',
        fileCount_other: '{{count}} files',
        sections: {
            email: 'Email',
            content: 'Extracted text',
            recaps: 'Recaps',
        },
        content: {
            hint: 'The text read from the files, which the recaps are drafted from.',
            empty: 'No text extracted',
            processing: 'Reading …',
        },
        recapsEmpty: {
            description: 'This source becomes a recap, on its own or together with other sources.',
        },
        syncErrorTitle: 'A connection couldn’t be fetched last time',
        syncError: 'You’ll find the details in the settings.',
        signedOutTitle_one: 'A connection needs to be reconnected',
        signedOutTitle_other: '{{count}} connections need to be reconnected',
        signedOut: 'Until then, nothing will come in through {{connections}}.',
        select: 'Select {{name}}',
        selectMode: 'Select',
        add: {
            button: 'Add',
            upload: 'Upload files',
            photo: 'Take a photo',
            link: 'From link',
            text: 'Paste text',
            pickFrom: 'Choose from',
            googleDrive: 'Google Drive',
            email: 'By email',
            teamsTranscript: 'Upload a Teams transcript',
            teamsTranscriptDescription:
                'A transcript you downloaded from Teams becomes a source with everyone who spoke. Zoom’s .vtt transcripts work too.',
            chooseFile: 'Choose file',
            emailTitle: 'By email',
            emailDescription:
                'Connect a mailbox (IMAP). “Fetch” imports new emails, and each one becomes a source, assigned to a client by the rules you set up for the mailbox.',
            emailDescriptionWithAddress:
                'Send or forward emails to the workspace’s address, and each one becomes a source. Clients and projects have addresses of their own (in their settings), which file what’s sent to them right away. Or connect a mailbox (IMAP), whose emails Recap assigns by the rules you set up.',
            connectedMailboxes: 'Connected: {{mailboxes}}',
            connectMailbox: 'Connect mailbox',
        },
        upload: {
            button: 'Upload files',
            drop: 'Drop files here',
            uploading: 'Uploading …',
            failed: 'Upload failed',
            rejectedTitle: 'Some files weren’t uploaded',
            rejectedType:
                'Only PDFs, images, emails, recordings, Word, Excel, PowerPoint and text files are supported: {{files}}',
            rejectedSize: 'Larger than {{size}} MB (recordings: {{audioSize}} MB): {{files}}',
        },
        filters: {
            search: 'Search',
            status: 'Status',
            allStatuses: 'All statuses',
            client: 'Client',
            allClients: 'All clients',
            withoutClient: 'No client',
            recap: 'Recap',
            allRecaps: 'All',
            withoutRecap: 'Not in a recap',
            inRecap: 'In a recap',
        },
        project: {
            assign: 'Assign project',
            change: 'Change project',
        },
        client: {
            button: 'Client and project',
            change: 'Change client and project',
            title_one: 'Client and project',
            title_other: 'Assign client and project',
            description_one: 'Which client and project does this source belong to?',
            description_other: 'Which client and project do the {{count}} selected sources belong to?',
        },
        files: {
            emailBody: 'Email text',
            pageCount_one: '{{count}} page',
            pageCount_other: '{{count}} pages',
            image: 'Image',
            recording: 'Recording',
            download: 'Download {{name}}',
            downloadButton: 'Download',
        },
        email: {
            from: 'From',
            to: 'To',
            cc: 'Cc',
            sent: 'Sent',
        },
        delete: {
            button: 'Delete',
            title: 'Delete source?',
            confirm:
                '“{{name}}” will be deleted along with all its files. Recaps made from it stay, including their citations. This can’t be undone.',
            submit: 'Delete',
            blocked: 'This source can’t be deleted.',
        },
    },
    emailAddresses: {
        title: 'Email address',
        address: 'Private address',
        renew: 'Create new address',
        renewTitle: 'Create a new address?',
        renewConfirm: '{{address}} will stop taking emails. Enter the new address wherever you used the previous one.',
        anyone: 'This address takes emails from anyone who knows it, so only share it with people and tools you trust, and create a new address if it gets out.',
        forwarding:
            'If you set up automatic forwarding in Gmail from the address you sign in to Recap with, we’ll email you Google’s confirmation link.',
        prefix: {
            title: 'Change prefix',
            description:
                'Only the part after the underscore decides where an email goes. The prefix before it is only for you, so you can recognise the address, for example by the name of the tool that sends to it. Emails to the previous prefix still arrive.',
            label: 'Prefix',
            hint: 'Without a prefix, the address starts with the short name. The address will be {{address}}.',
            submit: 'Change',
        },
        workspace: {
            hint: 'Each email to this address becomes a source. If it comes from one of your clients’ people, the source goes to their client right away.',
        },
        client: {
            hint: 'Each email to this address becomes a source of “{{client}}”.',
        },
        project: {
            hint: 'Each email to this address becomes a source of “{{project}}”.',
        },
        employee: {
            client: 'Documents by email',
            clientHint: 'Send or forward emails to this address to get them to the team at “{{workspace}}”.',
            projectHint: 'Emails to this address reach the team as documents for “{{project}}”.',
        },
    },
    support: {
        title: 'Report a problem',
        description: "Describe what happened and what you expected instead. We'll reply by email.",
        whatHappened: 'What happened?',
        sentAlong: 'Sent along',
        name: 'Name',
        email: 'Email',
        workspace: 'Workspace',
        workspaceWithRole: '{{workspace}} · {{role}}',
        roles: {
            owner: 'Owner',
            member: 'Member',
            employee: 'Client’s person',
        },
        page: 'Page',
        browser: 'Browser',
        browserOnSystem: '{{browser}} on {{system}}',
        errorLog: 'Error log',
        errorCount_one: '{{count}} error',
        errorCount_other: '{{count}} errors',
        submit: 'Report problem',
        sentTitle: 'Thanks for your report',
        sent: "We've got your report and will get back to you by email.",
    },
    errorPage: {
        errorEyebrow: 'Error',
        notFoundEyebrow: '404',
        errorText: "This page couldn't be loaded. Reload it, and if the error stays, report the problem to us.",
        errorTextSignedOut: "This page couldn't be loaded. Reload it or try again later.",
        notFoundText: "There's no page at this address. It may have been deleted, or the link is incomplete.",
        reload: 'Reload',
        errorLogSentAlong: 'The error log is sent along when you report the problem.',
    },
    enums: {
        extractionStatus: {
            processing: 'Processing',
            done: 'Done',
            failed: 'Failed',
        },
        sourceKind: {
            upload: 'Upload',
            email: 'Email',
            mobile: 'Mobile',
            document: 'Document',
            link: 'Link',
            ticket: 'Ticket',
            calendar_event: 'Appointment',
            meeting: 'Meeting',
            conversation: 'Conversation',
        },
    },
};

export default en;
